import { fullLabel } from "../components/WhereItIs";
import { UserName } from "@/components/UserName";
import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CodeWordPicker } from "@/components/CodeWordPicker";
import { prov, provTap } from "@/lib/provenance";
import { useAuth } from "@/context/AuthContext";
import { ShelfCleanupStrip } from "@/components/ShelfCleanupDays";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { notifyPdfNoStamp } from "@/lib/printNotice";
import { Plus, ChevronRight, ChevronDown, ChevronLeft, Tag, Move, RotateCw, Archive, Building2, Package, Loader2, QrCode, Eye, FileSpreadsheet, Search, Layers, Printer, X, Pencil, Check, AlertTriangle, Grid3x3, MapPin, MoreHorizontal, ArrowRightLeft, Clock, Boxes, SlidersHorizontal, Lock } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LocationImportDialog } from "@/components/LocationImportDialog";
import { AuthImg } from "@/components/AuthImg";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse, scanWarn } from "@/lib/scanFeedback";
import { printZplChunked, checkBrowserPrint, PrintAborted, ZEBRA_FLUSH_HINT } from "@/lib/zebra";
import { getPrintChunkSize } from "@/lib/printSettings";
import { usePrintJob } from "@/lib/usePrintJob";
import { usePrintConfirm } from "@/lib/usePrintConfirm";
import { MediaPicker } from "@/components/MediaPicker";
import { mediaLabelOf, mediaToSize, sizeToMedia } from "@/lib/tagMedia";

// Location/bin label sizes the backend can render (distinct from part-tag TAG_MEDIA).
// Single source so the two size dropdowns stay in lockstep.
const LOCATION_SIZE_OPTIONS = [
  { value: "auto", label: "Auto (by type default)" },
  { value: "4x3", label: "4×3 sticker (small bins)" },
  { value: "4x6", label: "4×6 hang tag (big totes)" },
  { value: "3x1", label: "3×1 barcode (ZD421 · tiny bins)" },
];
import { PrintStopBanner } from "@/components/PrintStopBanner";
import { PrintRemainderBanner } from "@/components/PrintRemainderBanner";
import { LabelsTodayBadge } from "@/components/LabelsTodayBadge";
import { ProblemMoveDrawer } from "@/components/ProblemMoveDrawer";
import { SiteIntegrityCard } from "@/components/SiteIntegrityCard";
import { EastApronSetup } from "@/components/EastApronSetup";
import { useScrollMemory } from "@/hooks/useScrollMemory";
import { AddShelvesDialog } from "@/components/places/AddShelvesDialog";
import { AssignSectorDialog } from "@/components/places/AssignSectorDialog";
import { RackContents } from "@/components/places/RackContents";
import { UnplacedRacksBand } from "@/components/places/UnplacedRacksBand";
import { ScopePrintDialog } from "@/components/places/ScopePrintDialog";
import { HandPrintDialog } from "@/components/places/HandPrintDialog";
import { RackingDialog } from "@/components/places/RackingDialog";
import { ArchiveTreeDialog } from "@/components/places/ArchiveTreeDialog";
import { ArchivedPlacesDialog, HISTORY_WORDS } from "@/components/places/ArchivedPlacesDialog";
import { ScanTextInput } from "@/components/ScanTextInput";
import { SearchCameraButton } from "@/components/search/engine";
import { AddHereSheet } from "@/components/places/AddHereSheet";
import { PlaceTemplatesDialog } from "@/components/places/PlaceDialogs";
import { PrintLabelsSheet } from "@/components/places/PrintLabelsSheet";
import { CAN_ADD, PLACE_WORDS, bayOf, kindOf, plainPath, rowWord } from "@/lib/placeWords";

const TYPE_ICON = { site: Building2, zone: MapPin, structure: Grid3x3, spot: Tag, container: Package };
const CHILD_TYPE = { site: "zone", zone: "structure", structure: "spot", spot: "spot" };

// Plain-JS flatten (no recursive JSX component) -> ordered visible rows.
function flatten(nodes, byParent, expanded, depth, out) {
  nodes.forEach((n) => {
    const kids = byParent[n.id] || [];
    out.push({ node: n, depth, hasKids: kids.length > 0 });
    if (!expanded[n.id]) return;
    if (n.racking === "rack") flattenBays(n, kids, byParent, expanded, depth + 1, out);
    else flatten(kids, byParent, expanded, depth + 1, out);
  });
}

// BAYS SHOW AS THEIR OWN STEP (his design, 21 Oct) — read off the codes already there; nothing new is stored
const LEVEL_ORDER = "0ABCDEFGHJKLMNPQRSTUVWXYZ";
function flattenBays(rack, kids, byParent, expanded, depth, out) {
  const bays = new Map();
  const rest = [];
  kids.forEach((k) => { const b = bayOf(k.short_code); if (b) bays.set(b, [...(bays.get(b) || []), k]); else rest.push(k); });
  [...bays.keys()].sort((a, b) => a - b).forEach((b) => {
    const levels = bays.get(b).sort((x, y) => LEVEL_ORDER.indexOf(x.short_code.slice(-1)) - LEVEL_ORDER.indexOf(y.short_code.slice(-1)));
    const id = `${rack.id}:bay:${b}`;
    out.push({ node: { id, virtual: "bay", rackId: rack.id, bay: b, name: `Bay ${b}`, short_code: `${rack.short_code}-B${b}`, type: "bay",
                       levels: levels.length, parts: levels.reduce((n, l) => n + (l.direct_part_count || 0), 0) }, depth, hasKids: true });
    if (expanded[id]) flatten(levels, byParent, expanded, depth + 1, out);
  });
  flatten(rest, byParent, expanded, depth, out);
}

function BayRow({ row, ctx }) {
  const { node, depth } = row;
  const open = ctx.expanded[node.id];
  return (
    <div className="flex items-stretch border-t border-slate-100 bg-slate-50/50" data-testid={`bay-row-${node.short_code}`}>
      {Array.from({ length: depth }).map((_, i) => <span key={i} className="w-[22px] shrink-0 border-l border-slate-100" aria-hidden="true" />)}
      <button type="button" data-testid={`expand-${node.short_code}`} onClick={() => ctx.toggle(node.id)}
        className="flex-1 flex items-center gap-2 py-2 pl-2 min-h-[44px] text-left hover:bg-slate-100 transition-colors">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span className="font-semibold text-sm text-slate-800">{node.name}</span>
        <span className="text-[10px] font-mono uppercase text-slate-400">bay · {node.levels} level{node.levels === 1 ? "" : "s"}</span>
        {node.parts > 0 && <Badge className="rounded-lg bg-amber-100 text-[#C76714] text-[9px] uppercase font-mono border-0">{node.parts} part{node.parts === 1 ? "" : "s"}</Badge>}
      </button>
      {ctx.canPrint && <PrintLabelsButton code={node.short_code} onClick={() => ctx.onPrintLabels(ctx.locById[node.rackId], "rack", node.bay)} />}
    </div>
  );
}

// ONE "PRINT LABELS" on every rack, bay, holder and glass rack row (his call, 22 Oct) — no hunting through menus
function PrintLabelsButton({ code, onClick }) {
  return (
    <Button data-testid={`print-labels-${code}`} size="sm" onClick={onClick}
      className="self-center rounded-lg h-8 px-2.5 mr-2 bg-[#1F1F1F] hover:bg-black text-white text-[12px] font-bold shrink-0">
      <Printer className="w-3.5 h-3.5 mr-1" />Print labels
    </Button>
  );
}

// "YOU ARE HERE" in plain names — Royalton › Yard › East Apron › Pallet Rack 1 › Bay 3 › Level C
function YouAreHere({ hereId, locById }) {
  if (!hereId) return null;
  const [rackId, , bay] = hereId.split(":");
  const node = locById[rackId];
  if (!node) return null;
  const path = plainPath(node, locById) + (bay ? ` › Bay ${bay}` : "");
  return (
    <div data-testid="places-you-are-here" className="px-3 py-2 border-b border-slate-100 bg-white text-[13px] text-slate-600">
      You are here: <b className="text-slate-900">{path}</b>
    </div>
  );
}

function LocRow({ row, ctx }) {
  const { node, depth, hasKids } = row;
  const { expanded, toggle, canManage, navigate, onAdd, onMove, onReissue, onArchive, onRename, canEditContainer, onEditContainer, canMoveContainer, types, colors, selectable, selected, onToggleSelect } = ctx;
  const Icon = TYPE_ICON[node.type] || Tag;
  const kind = ctx.kindOf(node);
  const canAddHere = canManage && (CAN_ADD[kind] || []).length > 0;
  const isOpen = expanded[node.id];
  const isContainer = node.type === "container";
  const [editing, setEditing] = useState(false);
  const [ct, setCt] = useState(node.container_type || "none");
  const [cc, setCc] = useState(node.color || "none");
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = () => { setCt(node.container_type || "none"); setCc(node.color || "none"); setEditing(true); };
  const saveEdit = async () => {
    setSavingEdit(true);
    const ok = await onEditContainer(node, { container_type: ct === "none" ? "" : ct, color: cc === "none" ? "" : cc });
    setSavingEdit(false);
    if (ok) setEditing(false);
  };

  // ---- primary status badge (kept inline) ----
  const occ = node.direct_part_count + node.child_count;
  // ---- parent-weighted typography for a scannable tree ----
  const nameCls = node.type === "site"
    ? "font-bold text-slate-900 text-[15px]"
    : (hasKids ? "font-semibold text-slate-800 text-sm" : "font-medium text-slate-700 text-sm");

  // ---- demoted secondary details (muted, one calm line) ----
  const sec = [];
  sec.push(<span key="type" data-testid={`kind-${node.short_code}`} className="uppercase" title={PLACE_WORDS[kind]?.line}>{rowWord(node, kind)}</span>);
  if (isContainer && node.container_type && ctx.typeMap?.[node.container_type]) {
    sec.push(<span key="ctype" data-testid={`ctype-${node.short_code}`}>{ctx.typeMap[node.container_type].label}</span>);
  }
  if (node.truck_count > 0) {
    sec.push(<span key="trucks" data-testid={`trucks-${node.short_code}`} className="text-sky-700 font-semibold">{node.truck_count} truck{node.truck_count === 1 ? "" : "s"}</span>);
  }
  if (isContainer && node.color && ctx.colorMap?.[node.color]) {
    sec.push(
      <span key="ccolor" data-testid={`ccolor-${node.short_code}`} title={ctx.colorMap[node.color].label} className="inline-flex items-center gap-1">
        <span className="w-2.5 h-2.5 rounded-full border border-slate-300" style={{ backgroundColor: ctx.colorMap[node.color].hex }} />
        {ctx.colorMap[node.color].label}
      </span>
    );
  }
  if (isContainer) {
    sec.push(node.parent_id
      ? <span key="inuse" data-testid={`inuse-${node.short_code}`} className="text-blue-600">In use</span>
      : <span key="free" data-testid={`free-${node.short_code}`} className="text-emerald-600">Free</span>);
  }
  if (isContainer && node.location_label) {
    sec.push(<span key="loc" data-testid={`loc-of-${node.short_code}`} className="break-words" title={node.location_label}>@ {node.location_label}</span>);
  }
  if (node.type !== "site") {
    const verified = node.label_printed_source === "confirm";
    const isFixed = node.type === "spot" || node.type === "structure";
    sec.push(
      verified
        ? <span key="printed" data-testid={`printed-${node.short_code}`} className="text-emerald-600" title={`Printed (confirmed) ${node.label_printed_at}`}>✓ printed</span>
        : node.label_printed_at
          ? <span key="unverified" data-testid={`printed-unverified-${node.short_code}`} className="text-slate-500" title={`Legacy stamp — a print was recorded before confirm tracking existed (${node.label_printed_at}). Recorded, not vouched for.`}>printed (legacy)</span>
          : isFixed
            ? <span key="unknown" data-testid={`unknown-${node.short_code}`} className="text-slate-400" title="Never tracked — no confirmed print on record (unknown, not 'not printed')">unknown</span>
            : <span key="unprinted" data-testid={`unprinted-${node.short_code}`} className="text-slate-400" title="No confirmed print on record">not printed</span>);
  }

  // ---- action availability (all consolidated into the ⋯ menu) ----
  const canPrintAll = hasKids && (isContainer || node.type === "structure" || node.type === "zone");
  const canAddChild = canManage && CHILD_TYPE[node.type] && node.type !== "container" && !node.racking;
  // HIS COMPLAINT 3: *"I need to add a shelf to a VCR and I have no idea how."* A rack is stored
  // as a container, and `node.type !== "container"` meant a container row never showed an Add
  // action anywhere, at any permission. The server could always do it — the path in was missing.
  const canAddShelves = canManage && (isContainer || node.type === "structure") && !node.racking;
  // A PALLET RACK IS GROWN ONLY BY THE RACKING BUILDER — nothing hand-made on it or under its positions.
  const canAddRacking = canManage && node.type === "zone";
  const isRack = canManage && node.racking === "rack";
  const printKind = node.racking === "rack" ? "rack" : (isContainer || kind === "shelf_unit") ? "holder" : null;
  const stationary = isContainer && node.stationary;
  // A BUILT-IN RACK IS NOT MOVED BY A SCAN — only re-assigned deliberately, from here.
  const canMoveRow = stationary
    ? canManage
    : (isContainer && canMoveContainer) || (!isContainer && canManage && node.type !== "site");
  const canReissueArchive = canManage && node.type !== "site";
  // A SITE INSIDE ANOTHER SITE IS A MISTAKE (production had a "YARD" site under Royalton) — that one can be archived
  const canArchive = canReissueArchive || (canManage && node.type === "site" && !!node.parent_id);

  return (
    <>
    <div className="flex items-stretch hover:bg-slate-50 border-t border-slate-100 group" data-testid={`loc-node-${node.short_code}`}>
      {/* indent guides — one faint rule per depth for a scannable tree */}
      {Array.from({ length: depth }).map((_, i) => (
        <span key={i} className="w-[22px] shrink-0 border-l border-slate-100" aria-hidden="true" />
      ))}
      <div className="flex-1 min-w-0 flex items-center gap-2 py-2 pr-2 pl-2 min-h-[48px]">
        {/* A TICK BOX ON EVERY ROW, IN BOTH TABS — racks, shelves, levels, spots, totes, crates.
            That one change answers his "I had to reprint them one at a time". A site is the only
            row without one: there is no sticker for a whole yard. */}
        {selectable && node.type !== "site" && (
          <label className="shrink-0 p-1.5 -m-1.5 cursor-pointer" title="Select for bulk print">
            <input type="checkbox" data-testid={`select-${node.short_code}`} checked={selected?.has(node.id) || false}
              onChange={() => onToggleSelect(node.id)} onClick={(e) => e.stopPropagation()}
              className="w-5 h-5 accent-[#B01E28] cursor-pointer align-middle" />
          </label>
        )}
        {hasKids
          ? <button data-testid={`expand-${node.short_code}`} onClick={() => toggle(node.id)} className="text-slate-400 shrink-0">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
          : <span className="w-4 shrink-0" />}
        <Icon className="w-4 h-4 text-[#B01E28] shrink-0" />

        <div className="min-w-0 flex-1">
          {/* primary line: name + code + status + holding */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${nameCls} truncate`}>{node.name}</span>
            <Badge className="rounded-lg bg-slate-100 text-slate-700 text-[9px] font-mono">{node.short_code}</Badge>
            {node.type !== "site" && (
              occ > 0
                ? <Badge data-testid={`occupied-${node.short_code}`} className="rounded-lg bg-amber-100 text-[#C76714] text-[9px] uppercase font-mono border-0">
                    {node.direct_part_count > 0 ? `${node.direct_part_count} part${node.direct_part_count === 1 ? "" : "s"}` : ""}
                    {node.direct_part_count > 0 && node.child_count > 0 ? " · " : ""}
                    {node.child_count > 0 ? `${node.child_count} inside` : ""}
                  </Badge>
                : <Badge data-testid={`empty-${node.short_code}`} className="rounded-lg bg-slate-100 text-slate-400 text-[9px] uppercase font-mono border-0">Empty</Badge>
            )}
            {node.holding && (
              <Badge data-testid={`holding-${node.short_code}`} className="rounded-lg bg-amber-100 text-amber-800 border border-amber-300 text-[9px] font-mono uppercase">Holding area · parked, not put away</Badge>
            )}
            {stationary && (
              <Badge data-testid={`builtin-${node.short_code}`} title="Built in — a scan can never move this. Re-assign it from its own menu."
                className="rounded-lg bg-slate-800 text-white border-0 text-[9px] font-mono uppercase inline-flex items-center gap-1">
                <Lock className="w-2.5 h-2.5" />Built in
              </Badge>
            )}
          </div>

          {/* editing container type/color → inline controls replace the secondary line */}
          {isContainer && editing ? (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`edit-container-${node.short_code}`}>
              <Select value={ct} onValueChange={setCt}>
                <SelectTrigger data-testid={`edit-ctype-${node.short_code}`} className="h-7 w-[150px] rounded-lg border-slate-200 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No type —</SelectItem>
                  {(types || []).map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={cc} onValueChange={setCc}>
                <SelectTrigger data-testid={`edit-ccolor-${node.short_code}`} className="h-7 w-[130px] rounded-lg border-slate-200 text-xs"><SelectValue placeholder="Color" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No color —</SelectItem>
                  {(colors || []).map((c) => <SelectItem key={c.key} value={c.key}><span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full border border-slate-300" style={{ backgroundColor: c.hex }} />{c.label}</span></SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid={`save-container-${node.short_code}`} size="sm" className="h-7 rounded-lg bg-emerald-600 hover:bg-emerald-700" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              </Button>
              <Button data-testid={`cancel-container-${node.short_code}`} size="sm" variant="outline" className="h-7 rounded-lg border-slate-200" onClick={() => setEditing(false)} disabled={savingEdit}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : sec.length > 0 && (
            <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[10px] font-mono text-slate-400 mt-0.5">
              {sec.map((el, i) => (
                <span key={i} className="inline-flex items-center gap-2">
                  {i > 0 && <span className="text-slate-300" aria-hidden="true">·</span>}
                  {el}
                </span>
              ))}
            </div>
          )}
        </div>

        {canAddHere && (
          <Button data-testid={`add-here-${node.short_code}`} size="sm" onClick={() => ctx.onAddHere(node)}
            className="rounded-lg h-8 px-2.5 bg-[#E0781A] hover:bg-[#C76714] text-white text-[12px] font-bold shrink-0">
            <Plus className="w-3.5 h-3.5 mr-0.5" />Add here
          </Button>
        )}
        {ctx.canPrint && printKind && <PrintLabelsButton code={node.short_code} onClick={() => ctx.onPrintLabels(node, printKind)} />}
        {/* all per-row actions consolidated into one ⋯ menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid={`row-menu-${node.short_code}`} size="sm" variant="ghost" className="rounded-lg h-8 w-8 p-0 text-slate-500 shrink-0" title="Actions">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem data-testid={`label-${node.short_code}`} onClick={() => (isContainer ? ctx.onPrint(node) : navigate(`/label/${node.id}`))}>
              {isContainer ? <Printer className="w-4 h-4 mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
              {isContainer ? "Print label" : "View / print QR label"}
            </DropdownMenuItem>
            {canPrintAll && !printKind && (
              <DropdownMenuItem data-testid={`print-all-${node.short_code}`} onClick={() => ctx.onPrintAllLabels(node)}>
                <Layers className="w-4 h-4 mr-2" />Print all position labels
              </DropdownMenuItem>
            )}
            {canPrintAll && (
              <DropdownMenuItem data-testid={`whats-on-${node.short_code}`} onClick={() => ctx.onRackContents(node)}>
                <Eye className="w-4 h-4 mr-2" />What's on it — shelf by shelf
              </DropdownMenuItem>
            )}
            {node.type !== "site" && (
              <DropdownMenuItem data-testid={`contents-${node.short_code}`} onClick={() => ctx.onContents(node)}>
                <Eye className="w-4 h-4 mr-2" />See what's inside
              </DropdownMenuItem>
            )}
            {canAddShelves && (
              <DropdownMenuItem data-testid={`add-shelves-${node.short_code}`} onClick={() => ctx.onAddShelves(node)}>
                <Layers className="w-4 h-4 mr-2" />Add shelves
              </DropdownMenuItem>
            )}
            {canAddRacking && (
              <DropdownMenuItem data-testid={`add-racking-${node.short_code}`} onClick={() => ctx.onRacking({ mode: "new", zone: node })}>
                <Grid3x3 className="w-4 h-4 mr-2" />Add pallet racking here
              </DropdownMenuItem>
            )}
            {isRack && (
              <>
                <DropdownMenuItem data-testid={`add-level-${node.short_code}`} onClick={() => ctx.onRacking({ mode: "level", rack: node })}>
                  <Plus className="w-4 h-4 mr-2" />Add a level on top
                </DropdownMenuItem>
                <DropdownMenuItem data-testid={`add-bays-${node.short_code}`} onClick={() => ctx.onRacking({ mode: "bays", rack: node })}>
                  <Plus className="w-4 h-4 mr-2" />Add bays on the end
                </DropdownMenuItem>
                <DropdownMenuItem data-testid={`add-floor-${node.short_code}`} onClick={() => ctx.onRacking({ mode: "floor", rack: node })}>
                  <Plus className="w-4 h-4 mr-2" />Add a floor position
                </DropdownMenuItem>
              </>
            )}
            {canManage && (
              <DropdownMenuItem data-testid={`rename-${node.short_code}`} onClick={() => onRename(node)}>
                <Pencil className="w-4 h-4 mr-2" />Edit name
              </DropdownMenuItem>
            )}
            {isContainer && canEditContainer && (
              <DropdownMenuItem data-testid={`edit-container-btn-${node.short_code}`} onClick={startEdit}>
                <Pencil className="w-4 h-4 mr-2" />Edit type / color
              </DropdownMenuItem>
            )}
            {canAddChild && (
              <DropdownMenuItem data-testid={`add-child-${node.short_code}`} onClick={() => onAdd(node)}>
                <Plus className="w-4 h-4 mr-2" />Add {CHILD_TYPE[node.type]}
              </DropdownMenuItem>
            )}
            {/* IS THIS A REAL PLACE, OR SOMEWHERE THINGS ARE PARKED ON THE WAY SOMEWHERE ELSE?
                The flag decides whether a truck sitting here reads as PLACED — and it was written
                by a migration years ago with nothing able to change it, which is how the fenced
                lot ended up reading like a truck nobody had ever placed. It's his yard, so it's
                his flag: a receiving dock genuinely is holding, a fenced lot genuinely is not. */}
            {canManage && !isContainer && (
              <DropdownMenuItem data-testid={`holding-toggle-${node.short_code}`}
                onClick={() => ctx.onHolding?.(node)}>
                <Clock className="w-4 h-4 mr-2" />
                {node.holding ? "Counts as a real place" : "Mark as a holding area"}
              </DropdownMenuItem>
            )}
            {canMoveRow && (
              <DropdownMenuItem data-testid={`move-${node.short_code}`}
                onClick={() => (stationary ? ctx.onAssignSector(node) : isContainer ? ctx.onContainerMove(node) : onMove(node))}>
                {stationary ? <MapPin className="w-4 h-4 mr-2" /> : <Move className="w-4 h-4 mr-2" />}
                {stationary ? "Put in a sector" : isContainer ? "Move (scan destination)" : "Reparent in structure"}
              </DropdownMenuItem>
            )}
            {canArchive && (
              <>
                <DropdownMenuSeparator />
                {canReissueArchive && (
                  <DropdownMenuItem data-testid={`reissue-${node.short_code}`} onClick={() => onReissue(node)}>
                    <RotateCw className="w-4 h-4 mr-2" />Reissue token
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid={`archive-${node.short_code}`} className="text-destructive focus:text-destructive" onClick={() => onArchive(node)}>
                  <Archive className="w-4 h-4 mr-2" />Archive{hasKids ? " (with everything under it)" : ""}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
    {ctx.rackContentsId === node.id && (
      <RackContents node={node} depth={depth} onClose={() => ctx.onRackContents(null)} />
    )}
    </>
  );
}

const UNPLACED = "Unplaced · free totes";

export default function LocationsPage({ initialView = "physical" }) {
  const { has, resolveName } = useAuth();
  const navigate = useNavigate();
  const canManage = has("manage_locations");
  const canResolveProblems = has("view_problem_moves");
  // NEVER LOSE YOUR PLACE. His complaint 2: *"When I went back to pick another rack, I had to
  // SEARCH FOR IT AGAIN."* The page read `q` and `view` from the address bar and never wrote them
  // back, and the filters, the sort and which racks were open were plain state. Now the ADDRESS IS
  // HIS PLACE — tab, search, every filter, the sort and the open racks all live in it, the sidebar
  // entry is `resumable` like Find a Part, and `useScrollMemory` puts him back on the same row.
  const P0 = new URLSearchParams(window.location.search);
  const [locs, setLocs] = useState([]);
  const [expanded, setExpanded] = useState(() => Object.fromEntries(
    (new URLSearchParams(window.location.search).get("open") || "").split(",").filter(Boolean).map((id) => [id, true])));
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", type: "zone", parent_id: null, address: "" });
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveParent, setMoveParent] = useState("");
  const [cmTarget, setCmTarget] = useState(null);
  const [cmDest, setCmDest] = useState("");
  const [cmBusy, setCmBusy] = useState(false);
  const [cmResult, setCmResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contents, setContents] = useState(null);
  const [contTitle, setContTitle] = useState("");
  const [contentsBusy, setContentsBusy] = useState(false);
  const [contentsNode, setContentsNode] = useState(null);
  const [contStack, setContStack] = useState([]);
  const [dlgMoveOpen, setDlgMoveOpen] = useState(false);
  const [dlgDest, setDlgDest] = useState("");
  const [dlgMoveBusy, setDlgMoveBusy] = useState(false);
  const [dlgMoveResult, setDlgMoveResult] = useState(null);
  const [query, setQuery] = useState(() => P0.get("q") || "");
  const [view, setView] = useState(() => P0.get("view") || initialView);      // physical | free | by_location | lot_crates | all | fixed
  const [sortBy, setSortBy] = useState(() => P0.get("sort") || "code");        // code | type | newest
  const [filterType, setFilterType] = useState(() => P0.get("type") || "all");
  const [filterColor, setFilterColor] = useState(() => P0.get("color") || "all");
  const [filterOcc, setFilterOcc] = useState(() => P0.get("holds") || "all");   // all | free | in_use | empty | occupied
  const [filterPrint, setFilterPrint] = useState(() => P0.get("labels") || "all");   // all | confirmed | legacy | none (label print status)
  const [filtersOpen, setFiltersOpen] = useState(false);   // tablet: filters live behind one button
  const [rackContentsId, setRackContentsId] = useState(null);
  const [shelvesTarget, setShelvesTarget] = useState(null);
  const [sectorTarget, setSectorTarget] = useState(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [handOpen, setHandOpen] = useState(false);
  const [printTarget, setPrintTarget] = useState(null); // container node
  const [printForm, setPrintForm] = useState({ size: "", format: "zpl", copies: 1 });
  const [printing, setPrinting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [noHome, setNoHome] = useState(0);        // live parts with no place recorded at all
  const [bulkCopies, setBulkCopies] = useState(1);
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const [containerMedia, setContainerMedia] = useState("hang_tag");  // print-time media (any TAG_MEDIA)
  const containerMediaLabel = mediaLabelOf(containerMedia);
  const containerMediaSize = mediaToSize(containerMedia);
  useEffect(() => {
    api.get("/search", { params: { scope: "parts", dq: "no_location", page_size: 1 } })
      .then(({ data }) => setNoHome(data?.parts_total ?? data?.total ?? 0))
      .catch(() => setNoHome(0));
  }, []);
  const printJob = usePrintJob();
  const { printConfirmDialog, confirmAndRun } = usePrintConfirm();
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm, setBatchForm] = useState(() => {
    const defaults = { count: 20, name_prefix: "TOTE", container_type: "", color: "", label_size: "" };
    try {
      const saved = JSON.parse(localStorage.getItem("fas_totegen_prefs") || "{}");
      return { ...defaults, ...saved, count: defaults.count };  // sticky vessel settings; count fresh each open
    } catch { return defaults; }
  });
  const [generating, setGenerating] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  // THE EXACT CODES A BATCH WILL GET, before it's made — a prefix that doesn't fit is refused, never cut
  const [batchPreview, setBatchPreview] = useState(null);
  useEffect(() => {
    if (!batchOpen) return undefined;
    const t = setTimeout(() => {
      api.get("/containers/batch/preview", { params: { name_prefix: batchForm.name_prefix || "TOTE", count: parseInt(batchForm.count, 10) || 1 } })
        .then(({ data }) => setBatchPreview(data))
        .catch((e) => setBatchPreview({ error: formatApiErrorDetail(e.response?.data?.detail) }));
    }, 250);
    return () => clearTimeout(t);
  }, [batchOpen, batchForm.name_prefix, batchForm.count, batchResult]);
  const [batchCopies, setBatchCopies] = useState(1);
  // One place that runs a paced Zebra batch with the shared STOP banner wired in. `media`
  // is REQUIRED: it's what the send layer checks the target printer's stock against.
  const runZplBatch = async (zplList, jobLabel, tId, successMsg, media) => {
    const signal = printJob.start(jobLabel, zplList.length);
    try {
      await printZplChunked(zplList, { chunkSize: getPrintChunkSize(), onProgress: (done, tot) => {
        printJob.progress(done, tot);
        toast.loading(`Printing ${done}/${tot} labels…`, { id: tId });
      }, signal, mediaType: media });
      toast.success(successMsg, { id: tId });
      return { ok: true, sent: zplList.length };
    } catch (e) {
      const sent = e.done ?? 0;
      if (e instanceof PrintAborted || e.aborted) {
        toast.error(`Stopped after ${sent} of ${e.total ?? zplList.length} labels — the ones that fully printed are recorded as printed. ${ZEBRA_FLUSH_HINT}`, { id: tId, duration: 12000 });
      } else {
        toast.error(`Printing failed — check the Zebra connection. (${e.message})`, { id: tId });
      }
      return { ok: false, sent };
    } finally {
      printJob.end();
    }
  };

  // Run-scoped remainder (same mechanism as Make Tags batch recovery): an interrupted run
  // keeps the EXACT unsent labels so "Print remaining N" finishes the job — nothing guessed.
  const [remainder, setRemainder] = useState(null);   // { title, media, confirmKind: "containers"|"locations", labels: [{zpl,id}] }
  const printRemainderNow = () => {
    if (!remainder) return;
    const { title, media, confirmKind, labels, onAllSent } = remainder;
    confirmAndRun({ title: `Print remainder · ${title}`, labelCount: labels.length, copies: 1, noun: "labels", mediaType: media }, async () => {
      const dev = await checkBrowserPrint();
      if (!dev.ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
      setRemainder(null);
      const tId = toast.loading(`Printing remaining ${labels.length} labels…`);
      const r = await runZplBatch(labels.map((l) => l.zpl), `Remainder · ${title}`, tId, `Printed the remaining ${labels.length} labels.`, media);
      const ids = [...new Set(labels.slice(0, r.sent).map((l) => l.id).filter(Boolean))];
      if (ids.length) {
        const path = confirmKind === "containers" ? "/containers/label-printed/confirm" : "/locations/label-printed/confirm";
        await api.post(path, confirmKind === "containers" ? { container_ids: ids } : { location_ids: ids }).catch(() => {});
      }
      if (!r.ok && r.sent < labels.length) setRemainder({ title, media, confirmKind, labels: labels.slice(r.sent), onAllSent });
      if (r.ok && onAllSent) onAllSent();
      load();
    });
  };

  // Grid builder: bulk-create a connex interior (N bays × M levels) or a rack's rows in one shot.
  const [gridOpen, setGridOpen] = useState(false);
  const [gridForm, setGridForm] = useState({ parent_scan: "", dim1_label: "Bay", dim1_code: "BAY", dim1_count: 6, dim2_label: "Level", dim2_code: "L", dim2_count: 4 });
  const [gridGenerating, setGridGenerating] = useState(false);
  const [gridResult, setGridResult] = useState(null);

  const runGrid = async () => {
    if (!gridForm.parent_scan.trim()) { toast.error("Type or scan the connex/rack code to build into."); return; }
    setGridGenerating(true);
    try {
      const { data } = await api.post("/locations/generate-grid", {
        parent_scan: gridForm.parent_scan.trim(),
        dim1_label: gridForm.dim1_label, dim1_code: gridForm.dim1_code, dim1_count: parseInt(gridForm.dim1_count, 10) || 1,
        dim2_label: gridForm.dim2_label, dim2_code: gridForm.dim2_code, dim2_count: parseInt(gridForm.dim2_count, 10) || 0,
      });
      setGridResult(data);
      toast.success(`${data.parent_code}: ${data.created} position(s) created (${data.skipped} already existed).`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGridGenerating(false); }
  };

  const printGridLabels = async () => {
    if (!gridResult?.labels?.length) return;
    confirmAndRun({ title: `Print grid ${gridResult.parent_code || ""}`.trim(), labelCount: gridResult.labels.length, copies: 1, noun: "position labels", mediaType: "sticker" }, async () => {
      const dev = await checkBrowserPrint();
      if (!dev.ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
      const tId = toast.loading(`Printing ${gridResult.labels.length} labels…`);
      const r = await runZplBatch(gridResult.labels.map((l) => l.zpl), `Grid ${gridResult.parent_code || ""}`.trim(), tId, `Printed ${gridResult.labels.length} position labels.`, "sticker");
      const gridIds = gridResult.labels.slice(0, r.sent).map((l) => l.id).filter(Boolean);
      if (gridIds.length) { await api.post("/locations/label-printed/confirm", { location_ids: gridIds }).catch(() => {}); load(); }
      if (!r.ok && r.sent < gridResult.labels.length) setRemainder({ title: `Grid ${gridResult.parent_code || ""}`.trim(), media: "sticker", confirmKind: "locations", labels: gridResult.labels.slice(r.sent) });
    });
  };

  // Reprint every fixed position (bays/levels/rows) under a rack/connex node — no re-generation.
  const printAllPositionLabels = async (node) => {
    const dev = await checkBrowserPrint();
    if (!dev.ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
    try {
      const { data } = await api.get(`/locations/${node.id}/position-labels`);
      if (!data.labels.length) { toast.error("No rack/connex positions found under this node."); return; }
      confirmAndRun({ title: `Print all positions · ${data.parent_code || node.short_code || ""}`.trim(), labelCount: data.count, copies: 1, noun: "position labels", mediaType: "sticker" }, async () => {
        const tId = toast.loading(`Printing ${data.count} labels…`);
        const r = await runZplBatch(data.labels.map((l) => l.zpl), `Positions · ${data.parent_code || node.short_code || ""}`.trim(), tId, `Printed ${data.count} position labels for ${data.parent_code}.`, "sticker");
        const posIds = data.labels.slice(0, r.sent).map((l) => l.id).filter(Boolean);
        if (posIds.length) { await api.post("/locations/label-printed/confirm", { location_ids: posIds }).catch(() => {}); load(); }
        if (!r.ok && r.sent < data.labels.length) setRemainder({ title: `Positions · ${data.parent_code || node.short_code || ""}`.trim(), media: "sticker", confirmKind: "locations", labels: data.labels.slice(r.sent) });
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runBatch = async () => {
    const count = parseInt(batchForm.count, 10);
    if (!count || count < 1 || count > 200) { toast.error("Enter a count between 1 and 200."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/containers/batch", {
        count, name_prefix: batchForm.name_prefix.trim() || "TOTE",
        container_type: batchForm.container_type || null,
        color: batchForm.color || null,
        label_size: batchForm.label_size || null,
      });
      setBatchResult(data);
      try {
        localStorage.setItem("fas_totegen_prefs", JSON.stringify({
          name_prefix: batchForm.name_prefix, container_type: batchForm.container_type,
          color: batchForm.color, label_size: batchForm.label_size,
        }));
      } catch { /* ignore */ }
      toast.success(`Generated ${data.count} container(s): ${data.containers[0]?.short_code} … ${data.containers[data.count - 1]?.short_code}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const printBatchLabels = async (format = "zpl") => {
    const ids = (batchResult?.containers || []).map((c) => c.id);
    if (!ids.length) return;
    const copies = parseInt(batchCopies, 10) || 1;
    const already = ids.filter((id) => batchResult?.containers?.find((c) => c.id === id)?.label_printed_at).length;
    confirmAndRun({ title: `Print container batch · ${containerMediaLabel}`, labelCount: ids.length, copies, alreadyPrinted: already, noun: format === "pdf" ? "labels (PDF)" : "labels", mediaType: containerMedia }, async () => {
      try {
        if (format === "pdf") {
          const res = await api.post("/containers/labels", { container_ids: ids, size: containerMediaSize, format: "pdf", copies }, { responseType: "blob" });
          window.open(URL.createObjectURL(res.data), "_blank");
          notifyPdfNoStamp();
          return;
        }
        const dev = await checkBrowserPrint();
        if (!dev.ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
        const { data } = await api.post("/containers/labels", { container_ids: ids, size: containerMediaSize, format: "zpl", copies });
        const tId = toast.loading(`Printing ${data.labels.length} labels…`);
        const r = await runZplBatch(data.labels.map((l) => l.zpl), `Container batch · ${containerMediaLabel}`, tId, `Printed ${data.labels.length} container labels.`, containerMedia);
        // honest partial: a container counts as printed once at least one of its labels fully
        // went out — stamp exactly those (a cancelled remainder is never marked printed)
        const doneIds = [...new Set(data.labels.slice(0, r.sent).map((l) => l.id).filter(Boolean))];
        if (doneIds.length) await api.post("/containers/label-printed/confirm", { container_ids: doneIds }).catch(() => {});
        if (!r.ok && r.sent < data.labels.length) setRemainder({ title: "Container batch", media: containerMedia, confirmKind: "containers", labels: data.labels.slice(r.sent) });
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Printing failed."); }
    });
  };

  const viewContents = async (l, { push = false } = {}) => {
    setContTitle(`${l.short_code} · ${l.where_words?.line || fullLabel(l) || l.name}`);
    setContents(null); setContentsBusy(true);
    setDlgMoveOpen(false); setDlgDest(""); setDlgMoveResult(null);
    if (push && contentsNode) setContStack((s) => [...s, contentsNode]);
    setContentsNode({ id: l.id, short_code: l.short_code, name: fullLabel(l) || l.name, type: l.type || "container" });
    try { const { data } = await api.get(`/locations/${l.id}/contents`); setContents(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setContentsBusy(false); }
  };
  const contentsBack = () => {
    const prev = contStack[contStack.length - 1];
    if (!prev) return;
    setContStack((s) => s.slice(0, -1));
    viewContents(prev);
  };
  const runDialogMove = async () => {
    if (!dlgDest) { toast.error("Scan the destination bin first."); return; }
    setDlgMoveBusy(true); setDlgMoveResult(null);
    try {
      const { data } = await api.post("/containers/move", { container_scan: contentsNode.short_code, location_scan: dlgDest,
                                                            provenance: provTap("places") });
      if (data.ok) {
        const line = `Moved ${data.container?.short_code} → ${data.destination?.short_code} · ${data.moved_parts} part${data.moved_parts === 1 ? "" : "s"}`;
        if (data.moved_parts === 0) { scanWarn("MOVED — BUT 0 PARTS CAME WITH IT"); toast.warning(`${line} — empty/wrong tote?`); }
        else { scanAccept(`${data.container?.short_code} → ${data.destination?.short_code}`); toast.success(line); }
        setDlgMoveResult({ ok: true, ...data });
        load();
      } else { scanRefuse(data.message || "THAT CONTAINER DIDN'T MOVE"); setDlgMoveResult({ ok: false, message: data.message }); }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      scanRefuse(msg || "THAT CONTAINER DIDN'T MOVE"); setDlgMoveResult({ ok: false, message: msg });
    }
    finally { setDlgMoveBusy(false); loadProblems(); }
  };

  // Row-level container move — scan/type-only, reuses the exact Move Container endpoint + confirmation.
  const runContainerMove = async () => {
    if (!cmDest) { toast.error("Scan the destination bin first."); return; }
    setCmBusy(true); setCmResult(null);
    try {
      const { data } = await api.post("/containers/move", { container_scan: cmTarget.short_code, location_scan: cmDest,
                                                            provenance: provTap("places") });
      if (data.ok) {
        const line = `Moved ${data.container?.short_code} → ${data.destination?.short_code} · ${data.moved_parts} part${data.moved_parts === 1 ? "" : "s"}`;
        if (data.moved_parts === 0) { scanWarn("MOVED — BUT 0 PARTS CAME WITH IT"); toast.warning(`${line} — empty/wrong tote?`); }
        else { scanAccept(`${data.container?.short_code} → ${data.destination?.short_code}`); toast.success(line); }
        setCmResult({ ok: true, ...data });
        load();
      } else { scanRefuse(data.message || "THAT CONTAINER DIDN'T MOVE"); setCmResult({ ok: false, message: data.message }); }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      scanRefuse(msg || "THAT CONTAINER DIDN'T MOVE"); setCmResult({ ok: false, message: msg });
    }
    finally { setCmBusy(false); loadProblems(); }
  };

  const [cfg, setCfg] = useState({ types: [], colors: [], prefixes: [] });
  const canManageLists = has("manage_container_lists");
  const addListItem = async (kind) => {
    const label = window.prompt(
      kind === "prefixes" ? "New prefix (short, e.g. SKID) — letters/numbers only:"
        : kind === "types" ? "New container type name (e.g. Gaylord):"
          : "New color name (e.g. Orange):");
    if (!label || !label.trim()) return null;
    const item = kind === "types" ? { label: label.trim(), default_size: "4x3" }
      : kind === "colors" ? { label: label.trim(), hex: "#6B7280" }
        : { label: label.trim() };
    const before = new Set((cfg[kind] || []).map((x) => x.key));
    const next = { types: cfg.types || [], colors: cfg.colors || [], prefixes: cfg.prefixes || [], [kind]: [...(cfg[kind] || []), item] };
    try {
      const { data } = await api.put("/container-config", next);
      setCfg(data);
      const added = (data[kind] || []).find((x) => !before.has(x.key)) || (data[kind] || [])[(data[kind] || []).length - 1];
      toast.success(`Added ${kind.slice(0, -1)} “${added?.label}”.${kind === "colors" ? " Fine-tune its hex in Container lists." : ""}`);
      return added || null;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add."); return null; }
  };
  const typeMap = useMemo(() => Object.fromEntries((cfg.types || []).map((t) => [t.key, t])), [cfg]);
  const colorMap = useMemo(() => Object.fromEntries((cfg.colors || []).map((c) => [c.key, c])), [cfg]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/locations"); setLocs(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const [problems, setProblems] = useState([]);
  const [problemsOpen, setProblemsOpen] = useState(false);   // collapsed by default every visit
  const [problemDetailId, setProblemDetailId] = useState(null);
  const loadProblems = () => api.get("/problem-moves").then(({ data }) => setProblems(data.items || [])).catch(() => {});
  const resolveProblem = async (id) => {
    try { await api.post(`/problem-moves/${id}/resolve`, {}); setProblems((p) => p.filter((x) => x.id !== id)); window.dispatchEvent(new Event("problem-moves-changed")); toast.success("Marked resolved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    api.get("/container-config").then(({ data }) => setCfg(data)).catch(() => {});
    loadProblems();
  }, []);

  // THE ADDRESS IS HIS PLACE. Everything that decides what he is looking at goes into the URL, so
  // the sidebar's resumable entry and the browser's own Back button both bring him back to the
  // same rack with the same filters — and `useScrollMemory` returns him to the same row.
  useEffect(() => {
    const p = new URLSearchParams();
    p.set("view", view);
    if (query.trim()) p.set("q", query.trim());
    if (sortBy !== "code") p.set("sort", sortBy);
    if (filterType !== "all") p.set("type", filterType);
    if (filterColor !== "all") p.set("color", filterColor);
    if (filterOcc !== "all") p.set("holds", filterOcc);
    if (filterPrint !== "all") p.set("labels", filterPrint);
    const open = Object.keys(expanded).filter((k) => expanded[k]).slice(0, 40);
    if (open.length) p.set("open", open.join(","));
    const next = `${window.location.pathname}?${p.toString()}`;
    if (next !== window.location.pathname + window.location.search) navigate(next, { replace: true });
  }, [view, query, sortBy, filterType, filterColor, filterOcc, filterPrint, expanded, navigate]);
  useScrollMemory(!loading);

  const byParent = useMemo(() => {
    const m = {};
    locs.forEach((l) => { const k = l.parent_id || (l.type === "container" ? "_containers" : "_roots"); (m[k] = m[k] || []).push(l); });
    return m;
  }, [locs]);

  const locById = useMemo(() => Object.fromEntries(locs.map((l) => [l.id, l])), [locs]);
  const sites = locs.filter((l) => l.type === "site");
  const allContainers = locs.filter((l) => l.type === "container");
  const physicalContainers = useMemo(() => allContainers.filter((l) => !l.is_lot_crate), [allContainers]);
  const lotCrates = useMemo(() => allContainers.filter((l) => l.is_lot_crate), [allContainers]);
  // CONTAINERS NOBODY HAS PUT ANYWHERE — crates and totes sitting on the floor with parts inside.
  // His instruction, 2026-10-17: *"Nothing without a home should ever be out of sight."* So the
  // count rides at the top of BOTH views, not only inside the containers tab.
  const homeless = useMemo(() => {
    const kids = {};
    locs.forEach((l) => { if (l.parent_id) (kids[l.parent_id] = kids[l.parent_id] || []).push(l); });
    const subtreeParts = (l) => (l.direct_part_count || 0)
      + (kids[l.id] || []).reduce((n, c) => n + subtreeParts(c), 0);
    const rows = allContainers.filter((c) => !c.parent_id && !c.archived);
    return { containers: rows.length, parts: rows.reduce((n, c) => n + subtreeParts(c), 0) };
  }, [allContainers, locs]);
  // Free-totes KPI counts only real physical containers with no assigned location.
  const freeToteCount = physicalContainers.filter((l) => !l.parent_id).length;
  const lotCrateCount = lotCrates.length;
  const locationLabel = (c) => {
    const p = c.parent_id ? locById[c.parent_id] : null;
    return p ? (fullLabel(p) || p.name) : null;
  };
  const toggle = (id) => { setExpanded((e) => ({ ...e, [id]: !e[id] })); if (!expanded[id]) setHereId(id); };
  // ONE WAY TO ADD ANYTHING, TEMPLATES, PRINT EVERYTHING IN IT (his design, 21 Oct)
  const [hereId, setHereId] = useState(null);
  const [addHere, setAddHere] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [rollChange, setRollChange] = useState(null);
  const loadTemplates = () => api.get("/place-templates").then(({ data }) => setTemplates(data.items)).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  // A NEW PLACE'S CODE IS PICKED AND SHOWN BEFORE CREATE — never cut (his rule, 21 Oct)
  const [codePick, setCodePick] = useState(null);
  const takesWord = ["site", "zone", "structure", "spot", "container"].includes(addForm.type);

  const openAdd = (parent, type) => {
    setAddForm({ name: "", type: type || (parent ? CHILD_TYPE[parent.type] : "site"), parent_id: parent?.id || null, address: "" });
    setAddOpen(true);
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/locations", {
        name: addForm.name, type: addForm.type,
        parent_id: addForm.type === "container" ? null : addForm.parent_id,
        address: addForm.type === "site" ? addForm.address : undefined,
        code_word: takesWord ? codePick?.word : undefined,
      });
      toast.success("Location created with QR token + short code");
      if (addForm.parent_id) setExpanded((e) => ({ ...e, [addForm.parent_id]: true }));
      setAddOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doMove = async () => {
    setSaving(true);
    try {
      await api.post(`/locations/${moveTarget.id}/move`, { new_parent_id: moveParent || null });
      toast.success("Moved — the existing label still resolves (token unchanged)");
      setMoveTarget(null); setMoveParent(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reissue = async (l) => {
    try { await api.post(`/locations/${l.id}/reissue-token`); toast.success("Token reissued — old label revoked"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const archive = (l) => setArchiveNode(l);
  const [archiveNode, setArchiveNode] = useState(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [rackingTarget, setRackingTarget] = useState(null);
  const [printSheet, setPrintSheet] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [renameHistory, setRenameHistory] = useState(null);
  const openRename = (n) => {
    setRenameTarget(n); setRenameVal(n.name || ""); setRenameHistory(null);
    api.get(`/locations/${n.id}/history`).then((r) => setRenameHistory(r.data.items)).catch(() => setRenameHistory([]));
  };
  const doRename = async () => {
    const name = renameVal.trim();
    if (!name || !renameTarget) return;
    setRenaming(true);
    try {
      await api.put(`/locations/${renameTarget.id}`, { name });
      toast.success("Renamed");
      setRenameTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRenaming(false); }
  };

  const canEditContainer = has("manage_containers");
  const editContainer = async (node, patch) => {
    try {
      const { data } = await api.patch(`/locations/${node.id}`, patch);
      setLocs((prev) => prev.map((l) => (l.id === node.id ? { ...l, container_type: data.container_type, color: data.color } : l)));
      toast.success("Container type / color updated");
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };

  const openPrint = (node) => { setPrintForm({ size: "", format: "zpl", copies: 1 }); setPrintTarget(node); };
  const runPrint = async () => {
    if (!printTarget) return;
    const copies = parseInt(printForm.copies, 10) || 1;
    // Resolve "Auto (by type default)" BEFORE routing — a 4x6-default type must reach the
    // hang-tag printer, not the sticker printer. The resolved size is also sent to the
    // backend so the ZPL and the destination can never disagree.
    const effSize = printForm.size || typeMap[printTarget.container_type]?.default_size || "4x3";
    confirmAndRun({ title: `Print label · ${printTarget.short_code}`, labelCount: 1, copies, alreadyPrinted: printTarget.label_printed_at ? 1 : 0, noun: printForm.format === "pdf" ? "labels (PDF)" : "labels", mediaType: sizeToMedia(effSize) }, async () => {
      setPrinting(true);
      try {
        if (printForm.format === "pdf") {
          const res = await api.post("/containers/labels", { container_ids: [printTarget.id], size: effSize, format: "pdf", copies }, { responseType: "blob" });
          window.open(URL.createObjectURL(res.data), "_blank");
          notifyPdfNoStamp();
          setPrintTarget(null); return;
        }
        const dev = await checkBrowserPrint();
        if (!dev.ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
        const { data } = await api.post("/containers/labels", { container_ids: [printTarget.id], size: effSize, format: "zpl", copies });
        const tId = toast.loading(`Printing ${data.labels.length} label(s)…`);
        const ok = await runZplBatch(data.labels.map((l) => l.zpl), `Container · ${printTarget.short_code}`, tId, `Printed ${data.labels.length} label(s) for ${printTarget.short_code}.`, sizeToMedia(effSize));
        // confirm-driven stamp: only when it actually printed (no abort/failure)
        if (ok) { await api.post("/containers/label-printed/confirm", { container_ids: data.container_ids || [printTarget.id] }).catch(() => {}); load(); }
        if (ok) setPrintTarget(null);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Printing failed."); }
      finally { setPrinting(false); }
    });
  };

  // WHOSE CALL IS "TEMPORARY"? His. Confirmed before it commits, because it changes how every
  // truck sitting there reads, and the reply says how many that is.
  const [holdTarget, setHoldTarget] = useState(null);
  const [holdBusy, setHoldBusy] = useState(false);
  const commitHolding = async () => {
    if (!holdTarget) return;
    setHoldBusy(true);
    try {
      const { data } = await api.patch(`/locations/${holdTarget.id}/holding`, { holding: !holdTarget.holding });
      toast.success(data.message);
      setHoldTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setHoldBusy(false); }
  };

  const ctx = {
    expanded, toggle, canManage, navigate, typeMap, colorMap,
    canEditContainer, onEditContainer: editContainer, types: cfg.types || [], colors: cfg.colors || [],
    canMoveContainer: has("manage_containers"),
    // A TICK BOX ON EVERY ROW, IN BOTH TABS. Part lots are the one exception: they are logical
    // groupings of parts from an intake lot, not physical tags you print.
    selectable: view !== "lot_crates",
    selected, onToggleSelect: (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }),
    onPrintAllLabels: printAllPositionLabels,
    onAdd: (n) => openAdd(n),
    onMove: (n) => { setMoveTarget(n); setMoveParent(n.parent_id || ""); },
    onContainerMove: (n) => { setCmTarget(n); setCmDest(""); setCmResult(null); },
    onReissue: reissue, onArchive: archive, onContents: viewContents, onPrint: openPrint,
    onRename: openRename, onHolding: (n) => setHoldTarget(n),
    onAddShelves: (n) => setShelvesTarget(n),
    onRacking: (t) => setRackingTarget(t),
    canPrint: has("generate_print_labels"), locById,
    onPrintLabels: (node, kind, bay) => node && setPrintSheet({ node, kind, bay }),
    onAssignSector: (n) => setSectorTarget(n),
    rackContentsId, onRackContents: (n) => setRackContentsId(n ? n.id : null),
    kindOf: (n) => kindOf(n, locById),
    onAddHere: (n) => { setHereId(n.id); setAddHere(n); },
  };
  // ONE PRINT PATH for everything this page makes or reprints — add-shelves, print-everything-
  // under-a-place, one code by hand. Keeps what was already right: paced sending, the STOP banner,
  // the exact remainder for a one-tap finish, and the printed stamp written only AFTER the browser
  // reports what actually came out, so a cancelled run is never recorded as printed.
  const printLabelList = (labels, title, media = "sticker", onAllSent = null) => {
    if (!labels?.length) return;
    confirmAndRun({ title, labelCount: labels.length, copies: 1, noun: "labels", mediaType: media }, async () => {
      const dev = await checkBrowserPrint();
      if (!dev.ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
      const tId = toast.loading(`Printing ${labels.length} labels…`);
      const r = await runZplBatch(labels.map((l) => l.zpl), title, tId, `Printed ${labels.length} labels.`, media);
      const ids = [...new Set(labels.slice(0, r.sent).map((l) => l.id).filter(Boolean))];
      if (ids.length) await api.post("/locations/label-printed/confirm", { location_ids: ids }).catch(() => {});
      // a holder job cut short keeps its follow-up: the slots' roll change comes once the remainder is out
      if (!r.ok && r.sent < labels.length) setRemainder({ title, media, confirmKind: "locations", labels: labels.slice(r.sent), onAllSent });
      if (r.ok && onAllSent) onAllSent();
      load();
    });
  };
  // PRINT THIS AND EVERYTHING IN IT — the holder's own label, then each slot in order. One size = one job; a
  // different slot size = the holders first, then the slots after the roll is changed (his call: 4×3 or 3×1).
  const printEverything = async (ids, { slotSize = "4x3", holderSize = null, title = "" } = {}) => {
    try {
      const { data } = await api.post("/places/print-order", { ids });
      if (!data.items.length) { toast.error("Nothing to print."); return; }
      const hSize = (it) => holderSize || typeMap[it.container_type]?.default_size || "4x3";
      const sizeOf = (it) => (it.is_holder ? hSize(it) : slotSize);
      const fetchLabels = async (list, size) => (await api.post("/locations/labels", { location_ids: list.map((i) => i.id), size, format: "zpl" })).data.labels;
      const sizes = [...new Set(data.items.map(sizeOf))];
      if (sizes.length === 1) { printLabelList(await fetchLabels(data.items, sizes[0]), `Everything in ${title}`, sizeToMedia(sizes[0])); return; }
      const holders = data.items.filter((i) => i.is_holder);
      const rest = data.items.filter((i) => !i.is_holder);
      const inside = await fetchLabels(rest, slotSize);
      printLabelList(await fetchLabels(holders, hSize(holders[0])), `${title} · the holder${holders.length === 1 ? "" : "s"}`, sizeToMedia(hSize(holders[0])),
        () => setRollChange({ size: slotSize, title, labels: inside }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // FROM THE PRINT LABELS SHEET — exactly the files that were previewed. Two sizes (a 4×3 holder, 3×1 slots) =
  // the holder first, then the slots after the roll is changed.
  const printFromSheet = (data, title) => {
    setPrintSheet(null);
    const [first, second] = data.sizes;
    const pick = (size) => data.items.filter((i) => i.size === size).map(({ zpl, id, code }) => ({ zpl, id, code }));
    // the roll-change card only once the holder's own job has really gone out
    printLabelList(pick(first), second ? `${title} · the holder` : title, sizeToMedia(first),
      second ? () => setRollChange({ size: second, title: data.place_code, labels: pick(second) }) : null);
  };
  const printOneByHand = async (value) => {
    try {
      const { data } = await api.get("/locations/label-zpl", { params: { value, size: mediaToSize(containerMedia) } });
      setHandOpen(false);
      printLabelList([{ zpl: data.zpl, id: data.id, code: data.code }],
        `${data.code}${data.resolved ? "" : " · TEST code (not a saved place)"}`, containerMedia);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clearSelected = () => setSelected(new Set());
  const BULK_CHUNK_MAX_LABELS = 600;   // keep each request well under the ~60s server ceiling
  const bulkPrintLabels = async (format = "zpl") => {
    const ids = [...selected];
    if (!ids.length) return;
    const copies = Math.max(1, Math.min(parseInt(bulkCopies, 10) || 1, 20));
    const totalLabels = ids.length * copies;
    const already = ids.filter((id) => locById[id]?.label_printed_at).length;
    confirmAndRun({ title: `Print selected · ${containerMediaLabel}`, labelCount: ids.length, copies, alreadyPrinted: already, noun: format === "pdf" ? "labels (PDF)" : "labels", mediaType: containerMedia }, async () => {
    setBulkPrinting(true);
    try {
      if (format === "pdf") {
        if (totalLabels > 700) { toast.error(`${totalLabels} is too many for one PDF — reduce the selection or copies, or use Print (Zebra), which streams big runs safely in chunks.`); return; }
        const res = await api.post("/locations/labels", { location_ids: ids, size: containerMediaSize, format: "pdf", copies }, { responseType: "blob" });
        window.open(URL.createObjectURL(res.data), "_blank");
        notifyPdfNoStamp();
        await load();
        return;
      }
      const dev = await checkBrowserPrint();
      if (!dev.ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
      const per = Math.max(1, Math.floor(BULK_CHUNK_MAX_LABELS / copies));   // auto-chunk so no request times out
      const chunks = [];
      for (let i = 0; i < ids.length; i += per) chunks.push(ids.slice(i, i + per));
      const allZpl = [];
      const allIds = [];
      const tId = toast.loading(`Building labels 0/${chunks.length} batch${chunks.length === 1 ? "" : "es"}…`);
      const signal = printJob.start(`Building ${totalLabels} labels · ${containerMediaLabel}`, totalLabels);
      try {
        for (let i = 0; i < chunks.length; i++) {
          if (signal.aborted) throw new PrintAborted(0, totalLabels);
          const { data } = await api.post("/locations/labels", { location_ids: chunks[i], size: containerMediaSize, format: "zpl", copies }, { signal });
          allZpl.push(...data.labels.map((l) => l.zpl));
          allIds.push(...data.labels.map((l) => l.id));
          toast.loading(`Building labels ${i + 1}/${chunks.length} batch${chunks.length === 1 ? "" : "es"}…`, { id: tId });
        }
        printJob.setLabel(`Place labels · ${containerMediaLabel}`);
        printJob.progress(0, allZpl.length);
        await printZplChunked(allZpl, { chunkSize: getPrintChunkSize(), onProgress: (done, tot) => {
          printJob.progress(done, tot);
          toast.loading(`Printing ${done}/${tot} labels…`, { id: tId });
        }, signal, mediaType: containerMedia });
        toast.success(`Printed ${allZpl.length} labels for ${ids.length} place(s).`, { id: tId });
        // confirm-driven stamp: reached only when the full run completed without abort
        await api.post("/locations/label-printed/confirm", { location_ids: ids }).catch(() => {});
      } catch (e) {
        if (e instanceof PrintAborted || e.aborted || e.name === "CanceledError" || e.code === "ERR_CANCELED" || signal.aborted) {
          // honest partial: places whose label(s) fully went out are recorded as printed
          const sentIds = [...new Set(allIds.slice(0, e.done ?? 0).filter(Boolean))];
          if (sentIds.length) await api.post("/locations/label-printed/confirm", { location_ids: sentIds }).catch(() => {});
          const restZpl = allZpl.slice(e.done ?? 0);
          if (restZpl.length) setRemainder({ title: "Selected places", media: containerMedia, confirmKind: "locations", labels: restZpl.map((z, i) => ({ zpl: z, id: allIds[(e.done ?? 0) + i] })) });
          toast.error(`Stopped after ${e.done ?? 0} of ${allZpl.length || totalLabels} labels — ${sentIds.length} place(s) that fully printed are recorded. ${ZEBRA_FLUSH_HINT}`, { id: tId, duration: 12000 });
        } else {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Printing failed.", { id: tId });
        }
      } finally {
        printJob.end();
      }
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Printing failed."); }
    finally { setBulkPrinting(false); }
    });
  };

  const siteRows = useMemo(() => { const out = []; flatten(sites, byParent, expanded, 0, out); return out; }, [sites, byParent, expanded]);

  // Fixed-view label print status filter: the tree can't prune honestly, so an active filter
  // switches to a flat list of matching fixed locations (same pattern as search results).
  const printFilterRows = useMemo(() => {
    if (view !== "fixed" || filterPrint === "all") return null;
    return locs
      .filter((l) => l.type !== "site" && l.type !== "container" && !l.archived)
      .filter((l) => (l.label_printed_source === "confirm" ? "confirmed" : (l.label_printed_at ? "legacy" : "none")) === filterPrint)
      .sort((a, b) => (a.short_code || "").localeCompare(b.short_code || ""))
      .map((n) => ({ node: n, depth: 0, hasKids: false }));
  }, [view, filterPrint, locs]);

  const containerGroups = useMemo(() => {
    let base;
    if (view === "free") base = physicalContainers.filter((l) => !l.parent_id);
    else if (view === "lot_crates") base = lotCrates;
    else if (view === "all") base = allContainers;
    else base = physicalContainers;              // physical | by_location
    const occ = (c) => (c.direct_part_count || 0) + (c.child_count || 0);
    let rows = base.filter((c) => {
      if (filterType !== "all" && (c.container_type || "") !== filterType) return false;
      if (filterColor !== "all" && (c.color || "") !== filterColor) return false;
      if (filterOcc === "free" && c.parent_id) return false;
      if (filterOcc === "in_use" && !c.parent_id) return false;
      if (filterOcc === "empty" && occ(c) > 0) return false;
      if (filterOcc === "occupied" && occ(c) === 0) return false;
      if (filterPrint !== "all") {
        const st = c.label_printed_source === "confirm" ? "confirmed" : (c.label_printed_at ? "legacy" : "none");
        if (st !== filterPrint) return false;
      }
      return true;
    });
    const cmp = {
      code: (a, b) => (a.short_code || "").localeCompare(b.short_code || ""),
      type: (a, b) => (typeMap[a.container_type]?.label || "~").localeCompare(typeMap[b.container_type]?.label || "~") || (a.short_code || "").localeCompare(b.short_code || ""),
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
    }[sortBy] || undefined;
    rows = [...rows].sort(cmp);
    const mk = (c) => ({ node: { ...c, location_label: locationLabel(c) }, depth: 0, hasKids: false });
    if (view === "by_location") {
      const groups = {};
      rows.forEach((c) => { const k = locationLabel(c) || UNPLACED; (groups[k] = groups[k] || []).push(mk(c)); });
      return Object.keys(groups).sort((a, b) => (a === UNPLACED ? -1 : b === UNPLACED ? 1 : a.localeCompare(b)))
        .map((label) => ({ label, rows: groups[label],
                           parts: groups[label].reduce((n, r) => n + (r.node.direct_part_count || 0), 0) }));
    }
    return [{ label: null, rows: rows.map(mk) }];
  }, [view, physicalContainers, lotCrates, allContainers, filterType, filterColor, filterOcc, filterPrint, sortBy, typeMap, locById]);

  const visibleCount = containerGroups.reduce((n, g) => n + g.rows.length, 0);
  useEffect(() => { setSelected(new Set()); }, [view]);
  const moveCandidates = locs.filter((l) => l.type !== "container" && l.id !== moveTarget?.id);

  const q = query.trim().toLowerCase();
  const searchRows = useMemo(() => {
    if (!q) return null;
    return locs
      .filter((l) => [l.short_code, l.name, l.type, fullLabel(l)].some((v) => (v || "").toLowerCase().includes(q)))
      .sort((a, b) => (a.short_code || "").localeCompare(b.short_code || ""))
      .map((n) => ({ node: n.type === "container" ? { ...n, location_label: locationLabel(n) } : n, depth: 0, hasKids: false }));
  }, [q, locs, locById]);

  // "SELECT ALL SHOWN" means exactly the rows on screen right now — whichever list that is, in
  // whichever tab. A site is never selectable: there is no sticker for a whole yard.
  const shownRows = searchRows || (view === "fixed" ? (printFilterRows || siteRows) : containerGroups.flatMap((g) => g.rows));
  const selectableIds = useMemo(() => (ctx.selectable ? shownRows.filter((r) => r.node.type !== "site" && !r.node.virtual).map((r) => r.node.id) : []), [shownRows, ctx.selectable]);
  const allVisibleSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelectAll = () => setSelected((s) => { const n = new Set(s); if (allVisibleSelected) selectableIds.forEach((id) => n.delete(id)); else selectableIds.forEach((id) => n.add(id)); return n; });
  const activeFilterCount = [filterType, filterColor, filterOcc, filterPrint].filter((f) => f !== "all").length;
  const clearFilters = () => { setFilterType("all"); setFilterColor("all"); setFilterOcc("all"); setFilterPrint("all"); };

  // THE SELECTION BAR BELONGS WHEREVER THE ROWS ARE. He finds a rack by SEARCHING for it, so the
  // search results need the tick boxes AND the print bar — the first build had the bar only on the
  // browse list, which would have left him ticking shelves with nothing to press.
  const bulkBar = ctx.selectable && selected.size > 0 ? (
    <div data-testid="bulk-select-bar" className="sticky bottom-0 z-20 flex flex-wrap items-center gap-2 px-3 py-2 bg-[#1F1F1F] text-white border-y border-black/20 shadow-[0_-6px_16px_rgba(0,0,0,0.25)]">
      <span className="font-mono text-xs" data-testid="bulk-selected-count">{selected.size} selected</span>
      {(() => { const ap = [...selected].filter((id) => locById[id]?.label_printed_at).length; return ap > 0 ? <span data-testid="bulk-already-printed" className="text-[11px] font-semibold text-amber-300">{ap} already printed</span> : null; })()}
      <button data-testid="bulk-clear" onClick={clearSelected} className="text-[11px] underline text-slate-300 hover:text-white">Clear</button>
      <div className="ml-auto flex items-center gap-2">
        <MediaPicker value={containerMedia} onChange={setContainerMedia} dark testidPrefix="bulk-media" />
        <Label className="text-[10px] uppercase tracking-wider text-slate-300 hidden sm:inline">Copies each</Label>
        <Input data-testid="bulk-copies-input" type="number" min={1} max={20} value={bulkCopies}
          onChange={(e) => setBulkCopies(e.target.value)} className="w-16 h-10 rounded-lg text-slate-900 bg-white" />
        <Button data-testid="bulk-print-pdf" size="sm" variant="outline" disabled={bulkPrinting}
          className="rounded-lg h-10 bg-transparent border-white/30 text-white hover:bg-white/10" onClick={() => bulkPrintLabels("pdf")}>
          <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />PDF
        </Button>
        <Button data-testid="bulk-print-zpl" size="sm" disabled={bulkPrinting}
          className="rounded-lg h-10 bg-[#E0781A] hover:bg-[#C76714]" onClick={() => bulkPrintLabels("zpl")}>
          {bulkPrinting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1" />}Print {selected.size}× (Zebra)
        </Button>
      </div>
    </div>
  ) : null;

  // HIS 10 BUILT-IN RACKS, which have no sector at all today and so appear nowhere in the tree.
  const unplacedRacks = useMemo(() => locs
    .filter((l) => l.type === "container" && l.stationary && !l.parent_id && !l.archived)
    .sort((a, b) => (a.short_code || "").localeCompare(b.short_code || "")), [locs]);
  // Everything a rack or a label scope can hang under / print from.
  const sectorChoices = useMemo(() => locs
    .filter((l) => ["site", "zone", "structure"].includes(l.type) && !l.archived)
    .sort((a, b) => (a.short_code || "").localeCompare(b.short_code || "")), [locs]);
  const scopeChoices = useMemo(() => locs
    .filter((l) => l.type !== "spot" && !l.archived && (l.child_count || 0) > 0)
    .sort((a, b) => (a.short_code || "").localeCompare(b.short_code || "")), [locs]);

  return (
    <div data-testid="locations-page">
      <PrintStopBanner job={printJob.job} onStop={printJob.stop} />
      {/* NOTHING WITHOUT A HOME IS EVER OUT OF SIGHT (his instruction, 2026-10-17). The unplaced
          CONTAINERS are listed first, in red, below; this strip is for parts with no place at all. */}
      {noHome > 0 && (
        <Link to="/parts?dq=no_location" data-testid="no-home-parts-strip"
          className="mb-3 flex items-center gap-2 rounded-lg border-2 border-[#B01E28] bg-red-50 px-3 py-2.5 text-sm font-bold text-[#B01E28] hover:bg-red-100 transition-colors">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {noHome} part{noHome === 1 ? "" : "s"} with no place recorded at all — nobody can walk to {noHome === 1 ? "it" : "them"}. Open the list
        </Link>
      )}
      {homeless.containers > 0 && (
        <button type="button" data-testid="no-home-containers-strip"
          onClick={() => { setView("by_location"); setTimeout(() => document.querySelector("[data-testid='containers-unplaced-header']")?.scrollIntoView({ behavior: "smooth", block: "center" }), 250); }}
          className="mb-3 w-full flex items-center gap-2 rounded-lg border-2 border-[#B01E28] bg-red-50 px-3 py-2.5 text-left text-sm font-bold text-[#B01E28] hover:bg-red-100 transition-colors">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {homeless.containers} container{homeless.containers === 1 ? "" : "s"} not on a shelf{homeless.parts > 0 ? ` — ${homeless.parts} part${homeless.parts === 1 ? "" : "s"} inside ${homeless.parts === 1 ? "it" : "them"}` : ""}. Open the list
        </button>
      )}
      {homeless.containers > 0 && <ShelfCleanupStrip />}
      {printConfirmDialog}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Places &amp; Containers</h1>
          <p className="text-sm text-slate-500">{view === "fixed"
            ? "The yard structure — sites, sectors, racks and the shelves inside them. Tick any rows and print their stickers together."
            : "The movable totes, bins, pallets and crates that hold parts. Move one and everything inside moves with it."}</p>
        </div>
        <div className="flex gap-2 items-center">
          <LabelsTodayBadge />
          {/* ONE PRINT MENU — it absorbs the whole separate screen whose only job was printing. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="print-menu" variant="outline" className="rounded-lg border-slate-200 h-10">
                <Printer className="w-4 h-4 mr-1.5" />Print <ChevronDown className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem data-testid="print-menu-selected" disabled={selected.size === 0} onClick={() => bulkPrintLabels("zpl")}>
                <Printer className="w-4 h-4 mr-2" />Selected ({selected.size})
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="print-menu-scope" onClick={() => setScopeOpen(true)}>
                <Layers className="w-4 h-4 mr-2" />Everything under one place…
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="print-menu-hand" onClick={() => setHandOpen(true)}>
                <QrCode className="w-4 h-4 mr-2" />Re-print one code by hand…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canManage && (
            <Button data-testid="place-templates-btn" variant="outline" className="rounded-lg border-slate-200 h-10" onClick={() => setTemplatesOpen(true)}>
              <Layers className="w-4 h-4 mr-1.5" />Templates
            </Button>
          )}
          {canManage && (
            <Button data-testid="archived-places-btn" variant="outline" className="rounded-lg border-slate-200 h-10" onClick={() => setArchivedOpen(true)}>
              <Archive className="w-4 h-4 mr-1.5" />Archived
            </Button>
          )}
          {canManage && (
            /* ONE NEW MENU — today's five header buttons in one list, so nothing is duplicated. */
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="new-menu" className="rounded-lg h-10 bg-[#E0781A] hover:bg-[#C76714] text-white">
                  <Plus className="w-4 h-4 mr-1.5" />New <ChevronDown className="w-3.5 h-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuItem data-testid="new-site" onClick={() => openAdd(null, "site")}>
                  <Building2 className="w-4 h-4 mr-2" />Property (a whole address)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="new-zone" onClick={() => openAdd(null, "zone")}>
                  <MapPin className="w-4 h-4 mr-2" />Zone (an area, a yard sector)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="new-row" onClick={() => setRackingTarget({ mode: "new" })}>
                  <Grid3x3 className="w-4 h-4 mr-2" />Pallet racking (bays × levels)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="new-grid" onClick={() => { setGridResult(null); setGridOpen(true); }}>
                  <Grid3x3 className="w-4 h-4 mr-2" />Shelves / levels into a rack
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="new-batch" onClick={() => { setBatchResult(null); setBatchOpen(true); }}>
                  <Layers className="w-4 h-4 mr-2" />Holders — totes, bins (a batch)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="new-container" onClick={() => openAdd(null, "container")}>
                  <Boxes className="w-4 h-4 mr-2" />One holder
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="new-import" onClick={() => setImportOpen(true)}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />Import a spreadsheet
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* THE ONLY VIEW SWITCH ON THE PAGE — two tabs. The fixed/movable split stays, because he
          set it himself and the floor put-away screen enforces it. */}
      <div className="flex gap-1 mb-5 border-b border-slate-200" role="tablist">
        {[["fixed", "Fixed places"], ["physical", "Containers"]].map(([v, label]) => {
          const on = v === "fixed" ? view === "fixed" : view !== "fixed";
          return (
            <button key={v} role="tab" aria-selected={on} data-testid={`places-tab-${v}`}
              onClick={() => setView(v)}
              className={`min-h-[48px] px-5 font-mono text-[11px] uppercase tracking-[0.15em] font-semibold border-b-2 -mb-[1px] transition-colors ${on ? "border-[#B01E28] text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
              {label}
            </button>
          );
        })}
      </div>

      {view === "fixed" && (
        <UnplacedRacksBand racks={unplacedRacks} canManage={canManage}
          onAssign={(r) => setSectorTarget(r)} onContents={(r) => setRackContentsId(r.id)} />
      )}
      {view === "fixed" && rackContentsId && unplacedRacks.some((r) => r.id === rackContentsId) && (
        <div className="mb-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <RackContents node={unplacedRacks.find((r) => r.id === rackContentsId)} onClose={() => setRackContentsId(null)} />
        </div>
      )}

      {view === "fixed" && canManage && <EastApronSetup onChanged={load} />}
      {view === "fixed" && canManage && <SiteIntegrityCard />}

      {/* KPI + search */}
      <div className="flex flex-wrap items-stretch gap-3 mb-5">
        <button data-testid="kpi-free-totes" onClick={() => { setQuery(""); setView("free"); }}
          title="Physical totes not placed anywhere — ready to grab for a pull."
          className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3 hover:bg-emerald-100 transition-colors text-left">
          <Package className="w-7 h-7 text-emerald-600" />
          <div>
            <div className="font-display text-3xl font-bold text-emerald-800 tabular-nums leading-none" data-testid="free-totes-count">{freeToteCount}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-700 mt-1">Free totes</div>
          </div>
        </button>
        <div className="flex items-center gap-1.5 flex-1 min-w-[240px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="location-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search everything — code, name, type, path (e.g. TOTE-007, shelf, R02)…"
              className="rounded-lg border-slate-200 pl-9 pr-9 h-full" />
            {query && (
              <button data-testid="clear-search" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <SearchCameraButton testid="location-search" onScan={setQuery} size="h-11" />
        </div>
      </div>

      <LocationImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />

      {problems.length > 0 && (
        <div className={`mb-5 rounded-xl border overflow-hidden ${problemsOpen ? "border-2 border-amber-300 bg-amber-50" : "border-amber-200 bg-amber-50/60"}`} data-testid="problem-moves-card">
          <button type="button" data-testid="problem-moves-toggle" onClick={() => setProblemsOpen((o) => !o)}
            className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-amber-100/50 transition-colors">
            <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse" />
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-amber-800 font-semibold">{problems.length} problem move{problems.length === 1 ? "" : "s"} need{problems.length === 1 ? "s" : ""} review</span>
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-amber-800 font-semibold shrink-0">{problemsOpen ? "Hide" : "Review"}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${problemsOpen ? "rotate-180" : ""}`} /></span>
          </button>
          {problemsOpen && (
          <div className="divide-y divide-amber-100 max-h-64 overflow-y-auto border-t border-amber-200">
            {problems.map((p) => (
              <div key={p.id} data-testid={`problem-move-${p.id}`} className="flex items-center gap-2 px-3 py-2 text-sm">
                <Badge className={`rounded-lg text-[9px] uppercase font-mono border-0 ${p.kind === "zero_parts" ? "bg-amber-200 text-amber-900" : "bg-red-200 text-red-900"}`}>
                  {p.kind === "zero_parts" ? "0 parts" : "error"}
                </Badge>
                <span className="flex-1 min-w-0">
                  <span className="font-mono text-xs">{p.container_code || p.container_scan || "?"}</span>
                  {p.dest_code && <span className="font-mono text-xs"> → {p.dest_code}</span>}
                  <span className="text-slate-500 block text-[11px] truncate" title={p.reason}>{p.reason}</span>
                  <span className="text-slate-400 text-[10px] font-mono"><UserName email={p.by} /> · {new Date(p.at).toLocaleString()}</span>
                </span>
                {canResolveProblems && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button data-testid={`problem-details-${p.id}`} size="sm" className="rounded-lg h-7 bg-[#1F1F1F] text-white hover:bg-black" onClick={() => setProblemDetailId(p.id)}>
                      <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />Details / Fix
                    </Button>
                    <Button data-testid={`resolve-problem-${p.id}`} size="sm" variant="outline" className="rounded-lg h-7 border-amber-300 text-amber-800" onClick={() => resolveProblem(p.id)}>
                      <Check className="w-3.5 h-3.5 mr-1" />Resolve
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
          <ProblemMoveDrawer id={problemDetailId} open={!!problemDetailId} onClose={() => setProblemDetailId(null)}
            onResolved={() => { loadProblems(); window.dispatchEvent(new Event("problem-moves-changed")); }} />
        </div>
      )}


      {searchRows ? (
        <div className="border border-slate-200 bg-white rounded-lg overflow-hidden" data-testid="search-results">
          <div className="bg-slate-100 px-3 py-2 flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <span>Search results · {searchRows.length}</span>
            {selectableIds.length > 0 && (
              <button data-testid="select-all-visible" onClick={toggleSelectAll} className="text-[10px] text-[#B01E28] underline normal-case tracking-normal min-h-[44px] px-1">
                {allVisibleSelected ? "Deselect all" : "Select all shown"}
              </button>
            )}
          </div>
          {bulkBar}
          {searchRows.length === 0
            ? <div className="p-8 text-center font-mono text-xs text-slate-400">NO MATCHES</div>
            : searchRows.map((r) => <LocRow key={r.node.id} row={r} ctx={ctx} />)}
        </div>
      ) : (
        <div className="border border-slate-200 bg-white rounded-lg overflow-hidden" data-testid="locations-workspace">
          <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50/60 flex flex-wrap items-center gap-2">
            {view !== "fixed" && (
              <>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 mr-1">Show</span>
                <Select value={view} onValueChange={setView}>
                  <SelectTrigger data-testid="container-view-select" className="h-10 w-[190px] rounded-lg border-slate-200 text-sm font-semibold"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="physical">Physical containers</SelectItem>
                    <SelectItem value="free">Free totes</SelectItem>
                    <SelectItem value="by_location">By location</SelectItem>
                    <SelectItem value="lot_crates">Part Lots{lotCrateCount ? ` (${lotCrateCount})` : ""}</SelectItem>
                    <SelectItem value="all">All containers</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            <span className="font-mono text-[10px] text-slate-400" data-testid="container-visible-count">
              {view === "fixed" ? `${sites.length} site${sites.length === 1 ? "" : "s"}` : `${visibleCount} shown`}
            </span>
            {selectableIds.length > 0 && (
              <button data-testid="select-all-visible" onClick={toggleSelectAll} className="font-mono text-[10px] text-[#B01E28] underline ml-1 min-h-[44px] px-1">
                {allVisibleSelected ? "Deselect all" : "Select all shown"}
              </button>
            )}
            <Button data-testid="filters-toggle" size="sm" variant="outline"
              className="ml-auto lg:hidden rounded-lg h-10 border-slate-200" onClick={() => setFiltersOpen((o) => !o)}>
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
            <div className={`lg:ml-auto w-full lg:w-auto flex flex-wrap items-center gap-2 ${filtersOpen ? "flex" : "hidden lg:flex"}`}>
              <Select value={filterPrint} onValueChange={setFilterPrint}>
                <SelectTrigger data-testid="filter-print-status" className="h-10 w-[170px] rounded-lg border-slate-200 text-xs"><SelectValue placeholder="Label print status" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="print-filter-opt-all">Label print: all</SelectItem>
                  <SelectItem value="confirmed" data-testid="print-filter-opt-confirmed">✓ Printed (confirmed)</SelectItem>
                  <SelectItem value="legacy" data-testid="print-filter-opt-legacy">Printed (legacy)</SelectItem>
                  <SelectItem value="none" data-testid="print-filter-opt-none">{view === "fixed" ? "Unknown (never tracked)" : "Not printed"}</SelectItem>
                </SelectContent>
              </Select>
              {view !== "fixed" && (
                <>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger data-testid="filter-container-type" className="h-10 w-[120px] rounded-lg border-slate-200 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent className="rounded-lg">
                      <SelectItem value="all">All types</SelectItem>
                      {(cfg.types || []).map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterColor} onValueChange={setFilterColor}>
                    <SelectTrigger data-testid="filter-container-color" className="h-10 w-[120px] rounded-lg border-slate-200 text-xs"><SelectValue placeholder="Color" /></SelectTrigger>
                    <SelectContent className="rounded-lg">
                      <SelectItem value="all">All colors</SelectItem>
                      {(cfg.colors || []).map((c) => <SelectItem key={c.key} value={c.key}><span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full border border-slate-300" style={{ backgroundColor: c.hex }} />{c.label}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterOcc} onValueChange={setFilterOcc}>
                    <SelectTrigger data-testid="filter-container-occ" className="h-10 w-[120px] rounded-lg border-slate-200 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-lg">
                      <SelectItem value="all">Any status</SelectItem>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="in_use">In use</SelectItem>
                      <SelectItem value="empty">Empty</SelectItem>
                      <SelectItem value="occupied">Occupied</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger data-testid="container-sort-select" className="h-10 w-[120px] rounded-lg border-slate-200 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-lg">
                      <SelectItem value="code">Sort: Code</SelectItem>
                      <SelectItem value="type">Sort: Type</SelectItem>
                      <SelectItem value="newest">Sort: Newest</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
              {activeFilterCount > 0 && (
                <button data-testid="clear-filters" onClick={clearFilters} className="font-mono text-[10px] text-[#B01E28] underline min-h-[44px] px-1">
                  Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </div>
          {bulkBar}
          {view === "lot_crates" && (
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-800 font-mono">
              Groupings of parts from intake lots — logical, not physical tags you print. Click one to see the parts inside.
            </div>
          )}
          {remainder && (
            <div className="px-3 pt-3">
              <PrintRemainderBanner remainder={remainder} onPrint={printRemainderNow} onDismiss={() => setRemainder(null)} />
            </div>
          )}
          {view === "fixed" ? (
            loading ? <div className="p-8 text-center font-mono text-xs text-slate-400">LOADING…</div>
              : printFilterRows ? (
                printFilterRows.length === 0
                  ? <div className="p-8 text-center font-mono text-xs text-slate-400" data-testid="fixed-print-filter-empty">NO FIXED LOCATIONS MATCH THIS LABEL PRINT STATUS</div>
                  : <>
                      <div className="bg-slate-100 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="fixed-print-filter-count">Label print status match · {printFilterRows.length}</div>
                      {printFilterRows.map((r) => <LocRow key={r.node.id} row={r} ctx={ctx} />)}
                    </>
              )
              : siteRows.length === 0 ? <div className="p-8 text-center font-mono text-xs text-slate-400" data-testid="fixed-empty">NO SITES YET</div>
              : <>
                  <YouAreHere hereId={hereId} locById={locById} />
                  {siteRows.map((r) => (r.node.virtual ? <BayRow key={r.node.id} row={r} ctx={ctx} /> : <LocRow key={r.node.id} row={r} ctx={ctx} />))}
                </>
          ) : visibleCount === 0 ? (
            <div className="p-8 text-center font-mono text-xs text-slate-400" data-testid="containers-empty">NONE MATCH THIS VIEW</div>
          ) : (
            containerGroups.map((g) => (
              <div key={g.label || "_"}>
                {g.label && (
                  <div data-testid={g.label === UNPLACED ? "containers-unplaced-header" : undefined}
                    className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] border-t ${g.label === UNPLACED
                      ? "bg-red-50 text-[#B01E28] border-red-100 font-bold" : "bg-slate-50 text-slate-500 border-slate-100"}`}>
                    {g.label} · {g.rows.length}
                    {g.label === UNPLACED && g.parts > 0 && (
                      <span data-testid="containers-unplaced-parts"> · {g.parts} part{g.parts === 1 ? "" : "s"} inside that nobody can walk to — put these somewhere</span>
                    )}
                  </div>
                )}
                {g.rows.map((r) => <LocRow key={r.node.id} row={r} ctx={ctx} />)}
              </div>
            ))
          )}
        </div>
      )}

      {/* Rename dialog (sector/zone/structure/spot) */}
      {/* IS THIS A REAL PLACE, OR SOMEWHERE THINGS ARE PARKED ON THE WAY SOMEWHERE ELSE?
          Written once by a migration with nothing able to change it, which is how the Fenced /
          Secured Lot came to be flagged holding. It's his yard, so it's his flag — and the dialog
          states the CONSEQUENCE, because the whole point of the flag is that it moves numbers. */}
      <Dialog open={!!holdTarget} onOpenChange={(v) => { if (!v) setHoldTarget(null); }}>
        <DialogContent data-testid="holding-confirm-dialog" className="rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle>
              {holdTarget?.holding
                ? `Make ${holdTarget?.name || holdTarget?.short_code} count as an ordinary place?`
                : `Mark ${holdTarget?.name || holdTarget?.short_code} as a holding area?`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 leading-relaxed">
            {holdTarget?.holding ? (
              <>Trucks sitting here will read simply as <b>PLACED</b>, with no holding mark and no
              separate count. Use this for anywhere a truck genuinely lives — a fenced lot, a
              staging sector, a row.</>
            ) : (
              <>A holding area is somewhere things are <b>parked on the way somewhere else</b> — a
              receiving spot, a dock. A truck here still counts as <b>placed and findable</b> (it
              never joins "no location"), but it is marked <b>parked, not put away</b> and lands in
              its own filter and its own count, so the put-away job stays visible.</>
            )}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoldTarget(null)} className="rounded-lg">Cancel</Button>
            <Button data-testid="holding-confirm-btn" onClick={commitHolding} disabled={holdBusy}
              className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {holdBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {holdTarget?.holding ? "Yes — it's a real place" : "Yes — it's a holding area"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) setRenameTarget(null); }}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="rename-location-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit name</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {renameTarget?.type === "zone" ? "Sector name" : `${renameTarget?.type || "Location"} name`}
            </Label>
            <Input data-testid="rename-input" autoFocus value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doRename(); }}
              className="rounded-lg border-slate-200" />
            <p className="text-[11px] text-slate-500" data-testid="rename-code-note">The code on its label, <b className="font-mono">{renameTarget?.short_code}</b>, stays exactly the same — every sticker keeps scanning.</p>
            {renameHistory?.length > 0 && (
              <div data-testid="rename-history" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600 space-y-0.5 max-h-32 overflow-y-auto">
                {renameHistory.map((h, i) => (
                  <div key={i}>{(h.at || "").slice(0, 16).replace("T", " ")} · {resolveName(h.by)} · {HISTORY_WORDS[h.action] || h.action}{h.before ? ` "${h.before}" → "${h.after}"` : ""}</div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button data-testid="save-rename-button" disabled={renaming || !renameVal.trim()} onClick={doRename}
              className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {renaming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="add-location-dialog">
          <DialogHeader><DialogTitle className="font-display">Add location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
              <Input data-testid="loc-name-input" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={addForm.type} onValueChange={(v) => setAddForm({ ...addForm, type: v })}>
                <SelectTrigger data-testid="loc-type-select" className="rounded-lg border-slate-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  {[["site", "property"], ["zone", "zone"], ["structure", "shelf_unit"], ["spot", "spot"], ["container", "holder"]].map(([t, k]) => (
                    <SelectItem key={t} value={t} data-testid={`loc-type-opt-${t}`}>{PLACE_WORDS[k].word} — {PLACE_WORDS[k].line}</SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
            {addForm.type === "site" && (
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
                <Input data-testid="loc-address-input" value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
            )}
            {addForm.type !== "site" && addForm.type !== "container" && (
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Parent</Label>
                <Select value={addForm.parent_id || ""} onValueChange={(v) => setAddForm({ ...addForm, parent_id: v })}>
                  <SelectTrigger data-testid="loc-parent-select" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="Select parent" /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    {locs.filter((l) => l.type !== "container").map((l) => <SelectItem key={l.id} value={l.id}>{fullLabel(l)}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            {addForm.type === "container" && <p className="text-[11px] font-mono text-slate-500">Containers float — they carry their own label and can be placed anywhere later.</p>}
            {takesWord && (addForm.type === "container" || addForm.type === "site" || addForm.parent_id) && (
              <CodeWordPicker name={addForm.name} type={addForm.type}
                parentId={["container", "site"].includes(addForm.type) ? null : addForm.parent_id} onResolved={setCodePick} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="save-location-button" disabled={saving || !addForm.name || (takesWord && !codePick)} onClick={create} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move dialog */}
      <Dialog open={!!moveTarget} onOpenChange={(v) => !v && setMoveTarget(null)}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="move-location-dialog">
          <DialogHeader><DialogTitle className="font-display">Move {moveTarget?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">The printed label keeps working — only the parent pointer changes.</p>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">New parent</Label>
            <Select value={moveParent} onValueChange={setMoveParent}>
              <SelectTrigger data-testid="move-parent-select" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="Select new parent" /></SelectTrigger>
              <SelectContent className="rounded-lg">
                {moveCandidates.map((l) => <SelectItem key={l.id} value={l.id}>{fullLabel(l)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setMoveTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-move-button" disabled={saving || !moveParent} onClick={doMove} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Move"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Row-level container move — SCAN/TYPE only (reuses Move Container logic + confirmation) */}
      <Dialog open={!!cmTarget} onOpenChange={(v) => { if (!v) { setCmTarget(null); setCmDest(""); setCmResult(null); } }}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="container-move-dialog">
          <DialogHeader><DialogTitle className="font-display">Move {cmTarget?.short_code}</DialogTitle></DialogHeader>
          {!cmResult ? (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">Scan / type the destination bin — the whole container + everything in it moves. Location is set by the scan, never a pick-list.</p>
              <ScanField testid="container-move-dest" label={null} submitLabel="Set bin" placeholder="Scan / type destination bin…"
                onScan={(v) => { setCmDest(v); scanAccept(`DESTINATION ${v}`); }} autoFocus={true} />
              <div className="flex gap-2 pt-1">
                <Button data-testid="container-move-cancel" variant="outline" className="flex-1 rounded-lg border-slate-200" onClick={() => setCmTarget(null)}>Cancel</Button>
                <Button data-testid="container-move-confirm" disabled={cmBusy || !cmDest} onClick={runContainerMove} className="flex-1 rounded-lg bg-[#1F1F1F] hover:bg-black text-white">
                  {cmBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Move {cmDest ? `→ ${cmDest}` : ""}</>}
                </Button>
              </div>
            </div>
          ) : (
            <div data-testid="container-move-result"
              className={`rounded-lg p-3 border-2 text-sm ${!cmResult.ok ? "border-red-500 bg-red-50 text-red-700" : cmResult.moved_parts === 0 ? "border-amber-500 bg-amber-50 text-amber-800" : "border-emerald-500 bg-emerald-50 text-emerald-800"}`}>
              {cmResult.ok ? (
                cmResult.moved_parts === 0 ? (
                  <div className="flex items-start gap-2" data-testid="container-move-empty-warning"><AlertTriangle className="w-5 h-5 shrink-0" /><div><div className="font-semibold">Moved <span className="font-mono">{cmResult.container?.short_code}</span> → <span className="font-mono">{cmResult.destination?.short_code}</span>, but 0 parts came with it.</div><div className="text-amber-700 mt-0.5">Empty container or wrong scan? Double-check.</div></div></div>
                ) : (
                  <div className="flex items-start gap-2"><Check className="w-5 h-5 shrink-0" /><div><div className="font-semibold">Moved <span className="font-mono">{cmResult.container?.short_code}</span> → <span className="font-mono">{cmResult.destination?.short_code}</span> · {cmResult.moved_parts} part{cmResult.moved_parts === 1 ? "" : "s"} came with it.</div><div className="text-emerald-700 mt-0.5">Now at <span className="font-mono">{fullLabel(cmResult.destination) || cmResult.destination?.short_code}</span>.</div></div></div>
                )
              ) : (
                <div className="flex items-center gap-2"><X className="w-5 h-5 shrink-0" />{cmResult.message}</div>
              )}
              <Button data-testid="container-move-done" variant="outline" className="w-full rounded-lg mt-2 h-9" onClick={() => setCmTarget(null)}>Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Contents dialog */}
      <Dialog open={!!contTitle} onOpenChange={(v) => { if (!v) { setContTitle(""); setContStack([]); setContentsNode(null); setDlgMoveOpen(false); setDlgDest(""); setDlgMoveResult(null); } }}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-lg" data-testid="contents-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base flex items-center gap-2">
              {contStack.length > 0 && (
                <button data-testid="contents-back" onClick={contentsBack} className="text-slate-400 hover:text-slate-700" title="Back">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              Inside {contTitle}
            </DialogTitle>
          </DialogHeader>
          {contentsBusy || !contents ? <div className="p-6 text-center font-mono text-xs text-slate-400">LOADING…</div> : (
            <div className="space-y-2 max-h-[60dvh] overflow-y-auto">
              <div className="font-mono text-[10px] uppercase text-slate-500" data-testid="contents-summary">{contents.part_count} part(s) · {contents.container_count} container(s)</div>
              {contentsNode?.type === "container" && canManage && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="dialog-move-panel">
                  {!dlgMoveOpen && !dlgMoveResult && (
                    <Button data-testid="dialog-move-open" size="sm" variant="outline" className="w-full rounded-lg border-slate-300 h-9"
                      onClick={() => setDlgMoveOpen(true)}>
                      <Move className="w-4 h-4 mr-1.5" />Move this container
                    </Button>
                  )}
                  {dlgMoveOpen && !dlgMoveResult && (
                    <div className="space-y-2">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Scan / type the destination bin — the whole container + its parts move</div>
                      <ScanField testid="dialog-move-dest" label={null} submitLabel="Set bin" placeholder="Scan / type destination bin…"
                        onScan={(v) => { setDlgDest(v); scanAccept(`DESTINATION ${v}`); }} autoFocus={true} />
                      <div className="flex gap-2">
                        <Button data-testid="dialog-move-cancel" size="sm" variant="outline" className="flex-1 rounded-lg border-slate-200" onClick={() => { setDlgMoveOpen(false); setDlgDest(""); }}>Cancel</Button>
                        <Button data-testid="dialog-move-confirm" size="sm" disabled={dlgMoveBusy || !dlgDest} onClick={runDialogMove} className="flex-1 rounded-lg bg-[#1F1F1F] hover:bg-black text-white">
                          {dlgMoveBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Move {dlgDest ? `→ ${dlgDest}` : ""}</>}
                        </Button>
                      </div>
                    </div>
                  )}
                  {dlgMoveResult && (
                    <div data-testid="dialog-move-result"
                      className={`rounded-lg p-2.5 border-2 text-sm ${!dlgMoveResult.ok ? "border-red-500 bg-red-50 text-red-700" : dlgMoveResult.moved_parts === 0 ? "border-amber-500 bg-amber-50 text-amber-800" : "border-emerald-500 bg-emerald-50 text-emerald-800"}`}>
                      {dlgMoveResult.ok ? (
                        dlgMoveResult.moved_parts === 0 ? (
                          <div className="flex items-start gap-2" data-testid="dialog-move-empty-warning"><AlertTriangle className="w-5 h-5 shrink-0" /><div><div className="font-semibold">Moved <span className="font-mono">{dlgMoveResult.container?.short_code}</span> → <span className="font-mono">{dlgMoveResult.destination?.short_code}</span>, but 0 parts came with it.</div><div className="text-amber-700 mt-0.5">Empty container or wrong scan? Double-check.</div></div></div>
                        ) : (
                          <div className="flex items-start gap-2"><Check className="w-5 h-5 shrink-0" /><div><div className="font-semibold">Moved <span className="font-mono">{dlgMoveResult.container?.short_code}</span> → <span className="font-mono">{dlgMoveResult.destination?.short_code}</span> · {dlgMoveResult.moved_parts} part{dlgMoveResult.moved_parts === 1 ? "" : "s"} came with it.</div><div className="text-emerald-700 mt-0.5">Now at <span className="font-mono">{fullLabel(dlgMoveResult.destination) || dlgMoveResult.destination?.short_code}</span>.</div></div></div>
                        )
                      ) : (
                        <div className="flex items-center gap-2"><X className="w-5 h-5 shrink-0" />{dlgMoveResult.message}</div>
                      )}
                      <Button data-testid="dialog-move-done" size="sm" variant="outline" className="w-full rounded-lg mt-2 h-8" onClick={() => { setContTitle(""); setContStack([]); setContentsNode(null); setDlgMoveOpen(false); setDlgDest(""); setDlgMoveResult(null); }}>Done</Button>
                    </div>
                  )}
                </div>
              )}
              {contents.containers.map((c) => (
                <button key={c.id} data-testid={`contents-child-${c.short_code}`} onClick={() => viewContents(c, { push: true })}
                  className="w-full flex items-center gap-2 text-sm border border-slate-200 rounded-lg px-2 py-2 hover:bg-slate-50 text-left">
                  <Package className="w-4 h-4 text-[#B01E28] shrink-0" />
                  <span className="font-mono text-xs">{c.short_code}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.is_lot_crate && <Badge className="rounded-lg bg-amber-100 text-amber-700 text-[8px] uppercase font-mono border-0">lot</Badge>}
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              ))}
              {contents.parts.map((p) => (
                <button key={p.id} data-testid={`contents-part-${p.short_code}`} onClick={() => navigate(`/parts/${p.id}`)}
                  className="w-full flex items-center gap-2.5 text-sm border-b border-slate-100 px-1 py-1.5 hover:bg-slate-50 text-left">
                  <div className="w-9 h-9 rounded-md bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                    {p.photo ? <AuthImg fileId={p.photo} thumb className="w-full h-full object-cover" alt={p.short_code} />
                      : <Package className="w-4 h-4 text-slate-300" />}
                  </div>
                  <span className="font-mono text-xs shrink-0">{p.short_code}</span>
                  <span className="flex-1 truncate">{p.name}</span>
                  <Badge className="rounded-lg text-[9px] uppercase font-mono shrink-0">{p.status}</Badge>
                </button>
              ))}
              {contents.part_count === 0 && contents.container_count === 0 && <div className="p-4 text-center font-mono text-xs text-slate-400" data-testid="contents-empty">EMPTY</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch generator dialog */}
      <Dialog open={batchOpen} onOpenChange={(v) => { if (!v) { setBatchOpen(false); setBatchResult(null); } }}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="batch-container-dialog">
          <DialogHeader><DialogTitle className="font-display">Generate totes / bins</DialogTitle></DialogHeader>
          {!batchResult ? (
            <>
              <p className="text-xs text-slate-500">Creates a batch of sequentially-numbered movable containers (e.g. <span className="font-mono">TOTE-001 … TOTE-020</span>), each with its own QR label. Run this anytime to make more — no fixed cap.</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">How many</Label>
                    <Input data-testid="batch-count-input" type="number" min={1} max={200} value={batchForm.count}
                      onChange={(e) => setBatchForm({ ...batchForm, count: e.target.value })} className="rounded-lg border-slate-200 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Prefix</Label>
                    <Select value={batchForm.name_prefix || ""} onValueChange={async (v) => {
                      if (v === "__add__") { const it = await addListItem("prefixes"); if (it) setBatchForm((f) => ({ ...f, name_prefix: it.label })); return; }
                      setBatchForm({ ...batchForm, name_prefix: v });
                    }}>
                      <SelectTrigger data-testid="batch-prefix-select" className="rounded-lg border-slate-200 mt-1 font-mono"><SelectValue placeholder="Pick a prefix" /></SelectTrigger>
                      <SelectContent className="rounded-lg">
                        {(cfg.prefixes || []).map((p) => <SelectItem key={p.key} value={p.label} className="font-mono">{p.label}</SelectItem>)}
                        {canManageLists && <SelectItem value="__add__" data-testid="batch-prefix-add">+ Add new prefix…</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {batchPreview?.error
                  ? <p data-testid="batch-prefix-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-[#B01E28]">{batchPreview.error}</p>
                  : <p data-testid="batch-code-preview" className="text-[11px] text-slate-500">Codes will be <span className="font-mono font-bold text-slate-700">{batchPreview?.first || "…"}</span> to <span className="font-mono font-bold text-slate-700">{batchPreview?.last || "…"}</span>. Vessels are born unplaced — scan one onto a shelf/truck to set its location.</p>}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
                    <Select value={batchForm.container_type || "none"} onValueChange={async (v) => {
                      if (v === "__add__") { const it = await addListItem("types"); if (it) setBatchForm((f) => ({ ...f, container_type: it.key })); return; }
                      setBatchForm({ ...batchForm, container_type: v === "none" ? "" : v });
                    }}>
                      <SelectTrigger data-testid="batch-type-select" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent className="rounded-lg">
                        <SelectItem value="none">— none —</SelectItem>
                        {cfg.types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                        {canManageLists && <SelectItem value="__add__" data-testid="batch-type-add">+ Add new type…</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Color</Label>
                    <Select value={batchForm.color || "none"} onValueChange={async (v) => {
                      if (v === "__add__") { const it = await addListItem("colors"); if (it) setBatchForm((f) => ({ ...f, color: it.key })); return; }
                      setBatchForm({ ...batchForm, color: v === "none" ? "" : v });
                    }}>
                      <SelectTrigger data-testid="batch-color-select" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent className="rounded-lg">
                        <SelectItem value="none">— none —</SelectItem>
                        {cfg.colors.map((c) => <SelectItem key={c.key} value={c.key}><span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full border border-slate-300" style={{ backgroundColor: c.hex }} />{c.label}</span></SelectItem>)}
                        {canManageLists && <SelectItem value="__add__" data-testid="batch-color-add">+ Add new color…</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Label size</Label>
                  <Select value={batchForm.label_size || "auto"} onValueChange={(v) => setBatchForm({ ...batchForm, label_size: v === "auto" ? "" : v })}>
                    <SelectTrigger data-testid="batch-size-select" className="rounded-lg border-slate-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-lg">
                      {LOCATION_SIZE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setBatchOpen(false)}>Cancel</Button>
                <Button data-testid="run-batch-button" disabled={generating || !!batchPreview?.error} onClick={runBatch} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Layers className="w-4 h-4 mr-1.5" />Generate</>}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div data-testid="batch-result">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                Created <span className="font-mono font-bold">{batchResult.count}</span> containers: <span className="font-mono">{batchResult.containers[0]?.short_code}</span> … <span className="font-mono">{batchResult.containers[batchResult.count - 1]?.short_code}</span>
              </div>
              <div className="mt-3 max-h-40 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
                {batchResult.containers.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm"><Package className="w-3.5 h-3.5 text-[#B01E28]" /><span className="font-mono text-xs">{c.short_code}</span><span className="text-slate-500">{c.name}</span></div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Copies per tag</Label>
                <Input data-testid="batch-copies-input" type="number" min={1} max={20} value={batchCopies}
                  onChange={(e) => setBatchCopies(e.target.value)} className="rounded-lg border-slate-200 w-20 h-8" />
                <span className="text-[11px] text-slate-400">e.g. big tote = 4</span>
              </div>
              <div className="mt-3">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Media</Label>
                <span className="inline-block mt-1 ml-2 align-middle"><MediaPicker value={containerMedia} onChange={setContainerMedia} testidPrefix="batch-media" /></span>
                <p className="text-[11px] text-slate-400 mt-1">Same code + QR + barcode — only the physical label format differs. Totes usually hang-tag; sticker for flat faces.</p>
              </div>
              <DialogFooter className="mt-3">
                <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => { setBatchOpen(false); setBatchResult(null); }}>Done</Button>
                <Button data-testid="print-batch-pdf" variant="outline" className="rounded-lg border-slate-200" onClick={() => printBatchLabels("pdf")}>
                  <Printer className="w-4 h-4 mr-1.5" />PDF
                </Button>
                <Button data-testid="print-batch-button" onClick={() => printBatchLabels("zpl")} className="rounded-lg bg-[#1F1F1F] hover:bg-black text-white">
                  <Printer className="w-4 h-4 mr-1.5" />Print (Zebra)
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Grid builder: connex interior (N bays × M levels) or a rack's rows */}
      <Dialog open={gridOpen} onOpenChange={(v) => { if (!v) { setGridOpen(false); setGridResult(null); } }}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-lg" data-testid="generate-grid-dialog">
          <DialogHeader><DialogTitle className="font-display">Generate a rack / connex interior</DialogTitle></DialogHeader>
          {!gridResult ? (
            <>
              <p className="text-xs text-slate-500">Bulk-create fixed positions in one shot — <b>N bays × M levels</b> inside a connex, or a rack's <b>rows</b> (set Levels to 0 for rows-only). Every position gets its own scannable/typeable code. Idempotent — re-running skips what already exists. Counts can differ per connex/rack.</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Build into (connex/rack code)</Label>
                  <ScanTextInput testid="grid-parent-input" value={gridForm.parent_scan}
                    onChange={(v) => setGridForm({ ...gridForm, parent_scan: v })}
                    placeholder="Scan or type e.g. ROY-CNX1 or ROY-NWC" className="mt-1" inputClassName="uppercase" />
                </div>
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">First dimension (bays / rows)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs text-slate-500">Label</Label><Input data-testid="grid-dim1-label" value={gridForm.dim1_label} onChange={(e) => setGridForm({ ...gridForm, dim1_label: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
                    <div><Label className="text-xs text-slate-500">Code</Label><Input data-testid="grid-dim1-code" value={gridForm.dim1_code} onChange={(e) => setGridForm({ ...gridForm, dim1_code: e.target.value })} className="rounded-lg border-slate-200 mt-1 font-mono" /></div>
                    <div><Label className="text-xs text-slate-500">How many</Label><Input data-testid="grid-dim1-count" type="number" min={1} max={60} value={gridForm.dim1_count} onChange={(e) => setGridForm({ ...gridForm, dim1_count: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Second dimension (levels · 0 = rows-only)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs text-slate-500">Label</Label><Input data-testid="grid-dim2-label" value={gridForm.dim2_label} onChange={(e) => setGridForm({ ...gridForm, dim2_label: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
                    <div><Label className="text-xs text-slate-500">Code</Label><Input data-testid="grid-dim2-code" value={gridForm.dim2_code} onChange={(e) => setGridForm({ ...gridForm, dim2_code: e.target.value })} className="rounded-lg border-slate-200 mt-1 font-mono" /></div>
                    <div><Label className="text-xs text-slate-500">Per bay</Label><Input data-testid="grid-dim2-count" type="number" min={0} max={60} value={gridForm.dim2_count} onChange={(e) => setGridForm({ ...gridForm, dim2_count: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400">Creates <b>{(parseInt(gridForm.dim1_count, 10) || 0) * ((parseInt(gridForm.dim2_count, 10) || 0) || 1)}</b> positions.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setGridOpen(false)}>Cancel</Button>
                <Button data-testid="run-grid-button" disabled={gridGenerating} onClick={runGrid} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
                  {gridGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Grid3x3 className="w-4 h-4 mr-1.5" />Generate grid</>}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div data-testid="grid-result">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <span className="font-mono font-bold">{gridResult.parent_code}</span> — {gridResult.created} created{gridResult.skipped ? `, ${gridResult.skipped} already existed` : ""} · {gridResult.dim1}{gridResult.dim2 ? ` × ${gridResult.dim2}` : ""}
              </div>
              <p className="text-xs text-slate-500 mt-2">Codes from <span className="font-mono">{gridResult.sample?.[0]}</span> to <span className="font-mono">{gridResult.sample?.[1]}</span>. Verify, then print.</p>
              <DialogFooter className="mt-3">
                <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => { setGridOpen(false); setGridResult(null); }}>Done</Button>
                <Button data-testid="grid-another-button" variant="outline" className="rounded-lg border-slate-200" onClick={() => setGridResult(null)}><Grid3x3 className="w-4 h-4 mr-1.5" />Another</Button>
                <Button data-testid="print-grid-button" onClick={printGridLabels} className="rounded-lg bg-[#1F1F1F] hover:bg-black text-white"><Printer className="w-4 h-4 mr-1.5" />Print {gridResult.labels.length} labels</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Per-container print dialog (size · format · copies) */}
      <Dialog open={!!printTarget} onOpenChange={(v) => !v && setPrintTarget(null)}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="dialog-print-label">
          <DialogHeader><DialogTitle className="font-display">Print label · {printTarget?.short_code}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Generic container tag — code, QR + barcode only. Location is never printed (the tote can move freely).</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Media size</Label>
              <Select value={printForm.size || "auto"} onValueChange={(v) => setPrintForm({ ...printForm, size: v === "auto" ? "" : v })}>
                <SelectTrigger data-testid="print-size-select" className="rounded-lg border-slate-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  {LOCATION_SIZE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Format</Label>
                <Select value={printForm.format} onValueChange={(v) => setPrintForm({ ...printForm, format: v })}>
                  <SelectTrigger data-testid="print-format-select" className="rounded-lg border-slate-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="zpl">Zebra (ZPL)</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Copies</Label>
                <Input data-testid="print-quantity-input" type="number" min={1} max={20} value={printForm.copies}
                  onChange={(e) => setPrintForm({ ...printForm, copies: e.target.value })} className="rounded-lg border-slate-200 mt-1" />
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Small tote = 1 · large tote = 4 · damaged-tag replacement = 2.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setPrintTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-print-button" disabled={printing} onClick={runPrint} className="rounded-lg bg-[#1F1F1F] hover:bg-black text-white">
              {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-1.5" />Print {printForm.copies || 1}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {shelvesTarget && (
        <AddShelvesDialog rack={shelvesTarget} existing={byParent[shelvesTarget.id] || []}
          onClose={() => setShelvesTarget(null)}
          onDone={() => { load(); setExpanded((e) => ({ ...e, [shelvesTarget.id]: true })); }}
          onPrint={(labels, title) => { setShelvesTarget(null); printLabelList(labels, title, "sticker"); }} />
      )}
      {sectorTarget && (
        <AssignSectorDialog node={sectorTarget} sectors={sectorChoices}
          onClose={() => setSectorTarget(null)} onDone={load} />
      )}
      <ScopePrintDialog open={scopeOpen} nodes={scopeChoices} onClose={() => setScopeOpen(false)}
        onPrint={(labels, title) => printLabelList(labels, title, "sticker")} />
      <HandPrintDialog open={handOpen} onClose={() => setHandOpen(false)} onPrintOne={printOneByHand} media={containerMedia} />
      {rackingTarget && (
        <RackingDialog target={rackingTarget} zones={locs.filter((l) => l.type === "zone" && !l.archived)}
          onClose={() => setRackingTarget(null)} onDone={load}
          onPrintRack={(r) => { setRackingTarget(null); setPrintSheet({ node: r, kind: "rack" }); }} />
      )}
      <ArchiveTreeDialog node={archiveNode} onClose={() => setArchiveNode(null)} onDone={load}
        onSeeContents={(placeId) => {
          const l = locs.find((x) => x.id === placeId);
          if (l) { setArchiveNode(null); viewContents(l); }
        }} />
      <ArchivedPlacesDialog open={archivedOpen} onClose={() => setArchivedOpen(false)} onDone={load} />
      {addHere && (
        <AddHereSheet node={addHere} kind={kindOf(addHere, locById)} where={plainPath(addHere, locById)} templates={templates}
          types={cfg.types || []} onClose={() => setAddHere(null)}
          onChanged={() => { load(); loadTemplates(); setExpanded((e) => ({ ...e, [addHere.id]: true })); }}
          onShelves={(n) => setShelvesTarget(n)} onPrint={printEverything} onPrintSheet={(node, kind) => setPrintSheet({ node, kind })} />
      )}
      {printSheet && <PrintLabelsSheet target={printSheet} onClose={() => setPrintSheet(null)} onPrint={printFromSheet} />}
      {templatesOpen && <PlaceTemplatesDialog onClose={() => setTemplatesOpen(false)} onChanged={loadTemplates} />}
      {rollChange && (
        <div data-testid="roll-change-card" className="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border-2 border-[#E0781A] bg-white p-4 shadow-xl">
          <div className="text-sm font-bold">Now load the {rollChange.size === "3x1" ? "3×1" : rollChange.size.replace("x", "×")} roll</div>
          <div className="text-[12px] text-slate-600 mt-1">Then print the {rollChange.labels.length} labels for what's inside {rollChange.title}, in order.</div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="roll-change-print" className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white"
              onClick={() => { const r = rollChange; setRollChange(null); printLabelList(r.labels, `${r.title} · inside`, sizeToMedia(r.size)); }}>
              Print {rollChange.labels.length} labels
            </Button>
            <Button data-testid="roll-change-later" variant="outline" className="rounded-lg" onClick={() => setRollChange(null)}>Not now</Button>
          </div>
        </div>
      )}
    </div>
  );
}
